/**
 * 업데이트 내역. (2026-08-19)
 *
 * 🔴 DB 에 두지 않는다
 *   무엇이 바뀌었는지는 코드가 바뀔 때 함께 적어야 맞다.
 *   DB 에 두면 배포와 내역이 따로 놀고, 롤백하면 내역만 남는다.
 *   여기 두면 코드와 같은 커밋에 실려 같이 배포되고 같이 되돌아간다.
 *
 * 🔴 사용자가 읽을 말로 적는다
 *   커밋 메시지를 옮겨오지 않는다. "V105 컬럼 추가" 가 아니라
 *   "추천이 종목의 왕복 성격을 함께 본다" 다.
 *   내부 구조는 docs/impl/ 이 담당한다.
 *
 * 최신이 위다. 버전은 화면 하단에 뜨는 VITE_APP_VERSION 과 같은 형식이다.
 */

export interface ChangelogEntry {
  /** 화면 하단 버전 표시와 같은 값. 날짜.빌드번호 */
  version: string;
  date: string;
  /** 한 줄씩. 사용자가 체감하는 변화만 적는다 */
  changes: string[];
  /**
   * 그날 오간 질문과 답. (2026-09-10 사용자 요청)
   *
   * 🔴 changes 와 무엇이 다른가
   *   changes 는 "무엇이 바뀌었나" 다. 이것은 "무엇을 묻고 무엇을 알아냈나" 다.
   *   코드가 안 바뀌어도 남는 것들이 있다 - 재봤더니 지금이 나아서 그대로 둔 결정,
   *   내가 잘못 알고 있다가 확인해서 정정한 것, 못 하기로 한 것과 그 이유.
   *   그런 것이 사라지면 몇 달 뒤 같은 질문을 다시 하게 된다.
   *
   * 없는 날은 넣지 않는다. 억지로 채우면 읽을 값어치가 떨어진다.
   *
   * 🔴🔴 **2026-09-23 개정 — 사용자의 질문은 넣지 않는다**
   *
   *   "이 대화 내용에 대해선 업데이트 알림에 넣지마. 나의 질문사항에 대해선
   *    업데이트 내역알림에 안 뜨도록 해."
   *
   *   이전 판(아래)은 "사항별로 허락을 받고 넣는다" 였다. 이제 **묻지 않고 넣지 않는다.**
   *   이 화면은 가족 모두가 본다. 사용자가 무엇을 궁금해했는지가 공개 화면에 남는 것 자체를
   *   원하지 않는다 — 특히 자산 규모·투입 금액 같은 것이 질문에 섞인다.
   *   질문에서 나온 **측정 결과**가 값어치 있으면 `docs/BACKLOG.md` 에 남긴다. 화면이 아니다.
   *
   *   🔴 `changes` 에도 질문을 옮겨 적지 않는다 — "이바다님이 물어서 찾았습니다" 같은
   *   경위는 괜찮지만(그 결함은 화면에 나간 변경이다), 질문 자체와 그 답은 넣지 않는다.
   *
   * 이하는 2026-09-11 판이다. 위 개정이 우선한다.
   *
   * 🔴 **문답은 사항별로 사용자 허락을 받고 넣는다** (2026-09-11 사용자 지시)
   *
   *   "가끔은 개인적인 궁금증도 너한테 물어볼 거거든. 기능 및 UI/UX 적인
   *    업데이트 외 궁금증에 대한 문답 사항들은 나한테 허락을 맡을 수 있도록 하자."
   *
   *   이 화면은 가족 모두가 본다. 대화에서 오간 질문 중에는 개인 자산·보유 종목
   *   같은 것이 섞이는데, 그것을 내가 임의로 판단해 골라 담으면 안 된다.
   *
   *   changes 는 허락이 필요 없다 - 화면에 실제로 나간 변경이라 이미 공개된 것이다.
   *   qna 만 커밋 전에 "이 문답 넣을까?" 를 항목마다 묻는다. 거절하면 넣지 않고,
   *   그 답은 대화에만 남는다.
   */
  qna?: ChangelogQna[];
}

export interface ChangelogQna {
  /** 사용자가 실제로 물은 것. 말투를 다듬되 뜻은 바꾸지 않는다 */
  q: string;
  /** 답. 근거가 숫자면 숫자를 적는다 */
  a: string;
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    // 10/20(화) 21:40 배포(release/1021 = 추천보고서 feat/ai-review-4 AIR v4 · 153 43절 · 화면 점검 고침 1d90b5a)
    //   1 ~ 3번째 줄(추천보고서 · 그 세션이 사용자에게 보인 글자 그대로) 사용자 승인 10/20 10:5x(추천보고서 세션 원문 "업데이트 내역 문안 세 줄은 그대로 승인할게." · 추천보고서 전언)
    //     ✏️ 1번째 줄 끝 문장 "글에서는 되풀이하지 않습니다" → "글에는 꼭 필요한 것만 씁니다"(화면 점검 — 실제 규칙은 칸마다 수량 2개까지) 사용자 승인 10/20 10:5x("위처럼 고치자.")
    //   🔴 버전 SEQ — 그날(10/20) 0시 뒤 main 커밋 수: release/1021 의 10/20 커밋 6 + 이 고침 1 + main 병합 1 = 8(main 만의 오늘 커밋 0 · 21:3x 셈 · v4 본체 a144389 등은 10/19 커밋이라 안 든다)
    version: '2026.10.20.8',
    date: '2026-10-20',
    changes: [
      'AI 2차 검토 보고서를 짧게 바꿨습니다. 맨 위 세 줄(어디서 빠졌나 · 이 종목만의 이야기 · 먼저 확인할 것)과 검토 초점을 먼저 보여 주고, 근거는 "관찰 · 의미 · 한계"로 나눠 4개 이하로 씁니다. 숫자는 위쪽 표에 두고 글에는 꼭 필요한 것만 씁니다.',
      'AI 의견(매수 검토 · 관망 · 피함) 표시를 없앴습니다. 예측을 하지 않는 보고서라 대부분 "관망"으로 나와 "사지 말라"는 뜻으로 오해될 수 있었습니다. 이전에 만든 보고서는 예전 모양 그대로 보입니다.',
      '회사 고유 악재는 그 회사가 주인공이거나 직접 걸린 자료를 실제로 읽을 수 있을 때만 "보임"으로 표시합니다. 같은 사건을 다룬 기사 여러 건은 사건 하나로 묶습니다.',
    ],
  },
  {
    // 10/19(월) 21:40 배포(release/1020 = 사용자 피드백 10/19 · 누적 수익률 · 스윙 화면 둘 + 추천보고서 feat/ai-review-3 · 관리자 AI 켜고 끄기)
    //   1 ~ 3번째 줄(5e) 사용자 승인 10/19 12:4x("업데이트 내역 문안 승인할게.")
    //   4 ~ 6번째 줄(추천보고서 AIR · 그 세션이 사용자에게 보인 글자 그대로) 사용자 승인 10/19 17:5x(5e 세션 · "내가 다 승인할테니까 너가 대신 추천보고서 세션에게 문안이랑 운영쓰기 허락한다고 해줘")
    //   🔴 버전 SEQ — 그날(10/19) 0시 뒤 main 커밋 수: release/1020 HEAD 23 + 이 고침 1 + main 병합 1 = 25(main 만의 오늘 커밋 0 · 21:2x 셈)
    version: '2026.10.19.25',
    date: '2026-10-19',
    changes: [
      '모의투자 계좌 카드의 큰 숫자가 원금 대비 누적 수익률로 바뀌었습니다. 4회차는 3회차의 예수금 · 보유를 그대로 이어받아, 회차 시작 기준으로만 보이던 손익에 지난 회차까지의 몫을 더해 보여 줍니다. 이번 회차 수익률은 그 밑에 작게 남고, 총자산은 "원금 + 지난 회차까지 + 실현 + 평가"로 읽습니다. 계좌 곡선과 비교 탭 순위는 계좌마다 시작한 회차가 달라 지금처럼 이번 회차 기준입니다.',
      '스윙 계좌 보유 표의 유예 · 만료 · 익절 표시를 한 줄에 하나씩 놓아 폰에서 종목 칸이 좁아지지 않게 했습니다.',
      '스윙(6개월) 탭 안내의 [보고서 보기]를 누르면 추천 탭의 🔎 저점 신호 검토 자리로 화면이 바로 내려갑니다. AI 호출이 꺼져 있을 때는 안내 문장도 기록만 남는다고 바뀝니다.',
      'AI 2차 검토 보고서가 근거 시각 처리 오류로 저장 전에 버려지던 문제, 1억~10억 원 숫자가 반올림돼 보이던 문제를 고쳤습니다. 공시 원문 발췌가 깨져 보이던 문제도 고쳤습니다(앞으로 받는 원문부터).',
      'AI 2차 검토는 이제 종목마다 웹 검색을 거쳐 씁니다. 하루 호출 상한을 30회로 늘렸습니다.',
      '관리자 화면(AI 비용 탭)에서 AI 2차 검토 호출을 켜고 끌 수 있습니다. 끄면 새 신호는 숫자 · 공시 · 기사 기록만 남고, 대기 중인 건은 다시 켤 때 처리됩니다.',
    ],
  },
  {
    // 10/18(일) 23:3x 2차 배포(release/1019 = feat/ai-review-2 AIR v3.1 · V156 + feat/llm-quality V157 + feat/swing-ai-mark · 사용자 10/18 21:4x "2차 배포에 같이" · 23:3x "승인할게. 바로 배포하자.")
    //   1 ~ 3번째 줄(AI 2차 검토 · 추천보고서 초안) 사용자 승인 10/18 21:4x(추천보고서 세션 · 원문 그대로)
    //     ✏️ 23:3x 1번째 줄 정정(화면 점검 — "그 순간의 숫자" 는 1차 11번째 줄에서 바로잡은 모호함 · 탭 이름 🔎) 사용자 승인("승인할게. 바로 배포하자.")
    //   4번째 줄(스윙 6개월 🔎 AI 2차 검토 표식 · feat/swing-ai-mark · 미확인 Q81) 사용자 승인 10/18 22:3x("업데이트 내역 문안 승인할게~")
    //   5번째 줄(회차 종합 분석 v2 · 154번 2.1 · 2.7) 사용자 승인 10/18 22:0x("회차 종합 분석 한 줄 문안(Q80)은 앞서 보여 준 그대로 승인할게")
    //   🔴 버전 SEQ — 자정 전 push 라 그날(10/18) 0시 뒤 main 커밋 수: release/1019 HEAD 87 + 이 고침 1 + main 병합 1 = 89(추천보고서 WIP 커밋까지 든다)
    version: '2026.10.18.89',
    date: '2026-10-18',
    changes: [
      '추천 탭 "🔎 저점 신호 검토" 카드에 AI 2차 검토 보고서가 붙습니다. 1단선에 닿은 순간의 현재가와 전날까지 확정된 숫자 · 공시 · 기사, AI의 웹 검색 결과를 묶어, 시장 · 섹터 · 종목이 각각 얼마나 움직였는지와 회사 자료를 정리하고 반대 논거와 "판단이 무효가 되는 조건"을 함께 씁니다. 문장마다 근거(공시 · 기사 원문 링크)가 붙고, 근거에 없는 숫자나 출처가 섞이면 저장 전에 빠지거나 보고서를 다시 씁니다. 계좌 매매와는 무관한 참고 자료이며 AI 의견은 실험 단계입니다.',
      '시장 전체가 크게 눌린 국면(KODEX 200이 고점에서 10% 이상 빠진 뒤 회복 전)이면 보고서 맨 위에 국면 며칠째 · 고점 대비 하락폭 · 최근 20일 움직임을 보여 주고, 이 종목이 평소 시장에 반응하던 정도로 본 참고 폭과 실제 하락의 차이를 나란히 적습니다. 원인 판정이 아니라 비교용 숫자입니다.',
      '하루 AI 호출 상한에 밀려 보고서를 못 받은 종목은 다음 거래일 저녁에 한 번 더 쓰며 "늦은 보고서" 표시가 붙습니다. 1단선 아래에 머무는 종목은 5거래일마다 다시 보되, 그사이 새 공시 · 기사가 없으면 AI를 부르지 않고 "새 공시 · 기사 없음"만 남깁니다.',
      '스윙(6개월) 탭에 🔎 표시가 붙었습니다. 이 계좌의 감시 종목이 AI 2차 검토 대상이라는 뜻입니다. 탭 위 안내의 [보고서 보기]를 누르면 추천 탭의 🔎 저점 신호 검토로 가고, 감시 표에서 1단선에 닿아 보고서가 열린 종목 옆에는 "🔎 n일째"가 붙어 누르면 그 보고서로 바로 갑니다. 물음표 도움말에도 AI 보고서가 언제 생기는지와 계좌 매매와는 무관하다는 설명을 더했습니다.',
      '회차가 끝날 때 AI가 쓰는 종합 분석이 4회차부터 코드가 센 거래 사실을 함께 봅니다. 손익분기 승률 · 손절이 몰린 날 · 장 끝 노출에 더해, 모든 청산을 "보유 중 시장(시장 평균 종목)이 −3% 이하였나 · 진입 전 20거래일이 −10% 이하였나"로 나눠 손절 비율을 셉니다. 이 나눔은 손절의 원인이 아니라 그때의 환경이라, AI는 숫자를 옮겨 쓰고 "~때문에" 같은 원인 판정은 하지 않습니다.',
    ],
  },
  {
    // 10/18(일) 06:05 배포(feat/toss-account → main · 토스 실계좌 더 쓰기 1 ~ 8 · I-100 · 150번) — 문안 7줄 사용자 승인 10/16 15:4x("업데이트내역문안 승인할게")
    //   ✏️ 10/16 17:1x 8번째 줄(미국 양도세 어림) 사용자 승인("그래 넣어도 돼") · 🔴 버전 SEQ 는 워크플로가 --since 그날 0시의 main 커밋 수로 만든다 — 브랜치 커밋은 10/16 이라 안 든다 · 배포 직전에 그날 커밋 수 + 병합 1 로 다시 센다
    //   ✏️ 10/17 18:3x 9번째 줄(시장지표 미국 모드 · Q17 · release/1018 로 일요일 배포) 사용자 승인("문안은 승인할게") — 지수 문장은 SPY · QQQ 구현에 맞춰 5e 가 고친 판(사용자에게 다시 보임)
    //   ✏️ 10/17 21:3x 10번째 줄(매매일지 원화 · 달러 따로 · Q23 · release/1018) 사용자 승인("업데이트 내역 승인할게")
    //   ✏️ 10/17 22:0x 11번째 줄(AI 2차 검토 0 · 1단계 · 저점 신호 검토 탭 · Q55 · 추천보고서 세션 초안) 사용자 승인("커밋할 부분과 업데이트 내역 문안 모두 승인할게")
    //     ✏️ 22:2x 정정 — 그 순간 값은 현재가뿐이고 수급 · 공매도 · 시장 숫자는 신호 전날까지 확정분(화면 점검 지적 · 추천보고서 정정안) · 사용자 승인("정정안 승인할게")
    //   ✏️ 10/18 10:5x 12번째 줄(거래마다 쓰던 AI 회고 멈춤 · be79997 · 3회차 결산 · 미확인 Q65 · Q66 ①) 사용자 승인("업데이트 내역 12번째 문안 승인할게")
    //   ✏️ 10/18 21:4x 버전 다시 셈 — 그날 0시 뒤 release/1018 커밋 3(be79997 · 6d801eb · cfc8a23) + 이 고침 1 + main 병합 1 = 5
    version: '2026.10.18.5',
    date: '2026-10-18',
    changes: [
      '계좌 화면 맨 위에 "마지막 동기화 n분 전" 과 [지금 동기화] 버튼이 생겼습니다. 화면을 열어 두면 지금처럼 1분마다 저절로 다시 받고, 버튼은 토스 앱에서 방금 주문한 것을 바로 볼 때 누릅니다. 토스에서 받지 못했으면 그 사실을 알려 드립니다.',
      '계좌 화면에 주문 가능 현금(원화 · 달러)과 수수료(국내 · 미국 · 행사가 끝나는 날)가 보입니다. 실제 수수료가 모의투자 계산과 다르면 표시가 붙고, 행사 요율이 끝나기 7 · 3 · 1일 전과 당일 아침에 관리자 알림이 갑니다.',
      '토스 앱에서 걸어 둔 조건주문(가격 · 수익률 감시)이 계좌 화면에 함께 보입니다. 조회만 하며, 걸기 · 고치기 · 취소는 토스 앱에서 합니다.',
      '보유 종목마다 우리 시스템 기준선이 한 줄 붙습니다 — 기간 범위 안 지금 위치 · 저점매수 사다리 ★ 1단 · 모의 저점매수가 감시 중이면 그 매수 기준가와 손절선. 추천이 아닙니다.',
      '내 실계좌 보유 종목에 분할 · 병합 의심이나 증자 공시가 뜨면 관리자 알림에 "내 실계좌 보유" 줄이 붙습니다.',
      '계좌 화면에 실계좌 누적 손익 곡선(판 것의 손익 + 들고 있는 것의 평가손익 · 원화 · 달러 따로)이 생겼습니다. 입금 · 출금은 들어가지 않습니다.',
      '매매일지에 미국 주식 양도세 어림 칸이 생겼습니다. 해마다(매도 결제일 기준) 미국 주식 이익 · 손실을 합쳐 기본공제를 빼고 세율을 곱한 예상 세액이며, 환율 · 증권사 계산 방식 등으로 실제 신고액과 다를 수 있습니다.',
      '매매일지에 산 날의 시스템 지표(120일 범위 위치 · 20일 이격도 · 저점매수 감시 여부)와 "산 자리별 성적" 칸이 생겼습니다. 승률은 10건부터 보입니다.',
      '시장지표의 국고채 · 지수 탭이 위 국내 / 미국 스위치를 따릅니다. 미국주식을 고르면 국고채 탭이 "미국 국채" 로 바뀌어 미국 국채 2년 · 10년 금리(미국 재무부 자료 · 다음 날 아침 반영)를 보여 주고, 지수 탭은 S&P 500 · 나스닥 100을 따르는 ETF(SPY · QQQ)의 하루 마지막 값(달러 · 미국 날짜)을 보여 줍니다 — 지수 포인트가 아니라 지수를 따르는 ETF 값이고, QQQ는 나스닥 종합이 아니라 나스닥 100입니다. 환율 · 비트코인 · 금은 두 시장이 같습니다.',
      '매매일지에서 미국 주식은 단가 · 손익 · 추천 목표 · 요약 합계를 달러로 따로 보여 주고, 원화와 섞거나 환율로 바꿔 합치지 않습니다.',
      '추천 탭에 "저점 신호 검토" 하위 탭이 생겼습니다. 국내 스윙 감시 종목이 1단선(120거래일 범위 하위 20%)에 닿으면 그 순간의 현재가와 전날까지 확정된 수급 · 공매도 · 시장 숫자, 최근 공시 · 기사를 종목 카드 하나에 모아 보여 줍니다. 계좌 매매와는 무관한 참고 자료이며, AI 의견 칸은 다음 업데이트에서 채워집니다.',
      '모의투자 계좌에서 거래마다 쓰던 AI 회고를 멈췄습니다. 3회차 회고를 모두 검토해 보니 숫자 기록 이상의 새 정보가 없었습니다. 지난 회고는 그대로 볼 수 있고, 회차가 끝날 때의 종합 분석은 계속 씁니다.',
    ],
  },
  {
    // 10/16 병합(feat/challenger → main · 4회차 계좌 개편) — 문안 9줄 사용자 승인 10/16 10:2x("업데이트 내역 문안 승인할게")
    //   1 · 4 · 6 은 10/10 · 10/11 승인 원문(1 은 끝에 "중단" 표시 한 구절) · 2 · 3 · 5 · 8 · 9 는 10/10 "그대로" 승인분을 지금 코드로 다시 쓴 문안 · 7 은 새 문안
    //   🔴 버전 SEQ 는 워크플로가 --since 오늘 0시의 main 커밋 수로 만든다 — 병합 직전에 다시 센다
    //   ✏️ 10/16 20:5x 다시 셈 — 그날 main 커밋 0 + 브랜치의 그날 커밋 8(fa9bf9a ~ 22c846d) + 이 고침 1 + 병합 1 = 10
    version: '2026.10.16.10',
    date: '2026-10-16',
    changes: [
      '🔄 4회차부터 분할익절 · 하이리스크 계좌를 잠정 중단합니다. 모의투자 계좌 탭 · 비교 탭 · 추천 목록 탭에서 빠지고, AI 회고와 회차 종합 분석에도 넣지 않습니다. 3회차까지의 성적과 기록은 회차 이력 · 지난 추천에 그대로 남고, 지난 추천 탭에는 "중단" 표시가 붙습니다.',
      '🔁 AI 모의투자에 국내 **스윙** 계좌 두 개가 생깁니다 — 스윙(6개월) · 스윙(3개월). 4회차부터 정해 둔 다섯 섹터의 종목을 범위 아래쪽에서 최대 3번 나눠 사고, 오를 때마다 나눠 팔며, 오래 들면(최대 42거래일) 정리합니다. 두 계좌는 진입선을 잡는 기간(최근 6개월 · 3개월)과 단별 금액 비율만 다릅니다. 스윙 탭에서 감시 대상 · 보유 · 못 산 이유를 볼 수 있고, 비교 탭에도 두 곡선이 그려집니다.',
      '🇺🇸 미국 모의투자에 **미국 스윙** 계좌가 생깁니다. 국내 스윙(6개월)과 같은 규칙으로 미국 대형주 다섯 섹터에서 4회차부터 사고팔며, 계좌 금액은 원화 · 가격은 달러로 보여 드립니다.',
      "🇺🇸 미국 모의투자에 '미국 추천' 계좌가 생겼습니다. 4회차부터 덜 출렁이는 미국 대형주가 오름세 속에서 살짝 눌렸을 때 사고, 계좌 금액은 원화 · 종목 표는 달러로 보여 드립니다.",
      '4회차부터 국내 안전 추천 계좌는 이미 들고 있는 종목이 새로 추천돼도 다시 사지 않습니다. 한 종목에 두 몫이 들어가던 일을 막습니다.',
      '주식 분할 · 병합이 의심되면 그 종목은 모든 모의 계좌에서 매매를 잠시 멈추고, 관리자가 확인한 뒤 다시 엽니다.',
      '장 시작 전(07:25 · 07:50 · 08:35)에 그날 기준가를 확인해, 5:4 분할 · 무상증자처럼 가격 제한폭 안에서 기준가가 바뀐 종목도 미리 멈춥니다. 확인하지 못한 날은 08:00~09:00 국내 장전 매매를 쉬고, 09:00부터 평소대로 돕니다.',
      '종목 차트의 봉 단위 아래에 기간 버튼(3개월 · 6개월 · 1년 · 3년)이 생겼습니다. 처음에는 3개월이 보이고, 종목을 바꾸면 다시 3개월로 돌아갑니다.',
      '자동매매 기록 표의 계좌 칸에 영어 키 대신 계좌 이름이 보입니다.',
    ],
  },
  {
    // 10/9 병합(feat/challenger → main · 4회차 준비) — 그날 main 커밋 0 + 브랜치의 그날 커밋 9(내역 3f9af9d · 일괄 알림 83f6f49 · 주도주 100 c313852 · 점검 결정 기록 포함) + 병합 1 = 10
    //   (워크플로 SEQ 는 --since 오늘 0시의 전체 커밋 수 · 병합 직전에 다시 센다 · ✏️ .7 → .8 → .9 → .10 커밋이 늘 때마다)
    //   문안 = docs/impl/139 0-4 "확정 문안" 26줄 그대로(1~25 는 10/8 12:0x "합본으로 할게" · 26 은 10/9 00:0x 사용자 승인) + 27 일괄 알림(I-93 · 10/9 03:2x 문안 사용자 승인) + 28 주도주 100(10/9 08:4x 문안 사용자 승인 · ✏️ 처음에 08:1x 로 잘못 적었다 — date 로 재지 않은 추정 시각)
    //   ✏️ 10/9 19:5x 병합 전 화면 점검(docs/ui-sweep/2026-10-09-feat-challenger.md) — 3번 줄 끝 "기록에 계좌 이름이 보입니다." 를 뺐다(기록 표에 계좌 칸이 없어 거짓 · 사용자 승인 (가) · 칸은 BACKLOG) · 고침 커밋 1 이 늘어 .10 → .11
    version: '2026.10.09.11',
    date: '2026-10-09',
    changes: [
      '회차가 끝나도 보유 종목을 팔지 않습니다. 3회차가 끝나는 10/16부터 보유 종목은 그대로 다음 회차로 넘어가고, 마감일에는 그날 평가액으로 성적표만 적습니다. 계좌 카드의 "시드" 자리에는 그 회차를 시작할 때의 금액이 보입니다.',
      '자동매매가 주문 직전에 한도를 한 번 더 확인합니다 — 종목당 누적 매수 금액 · 오늘 손실(실현+평가) · 전체 투자 금액 · 하루 매수 횟수. 전체 투자 한도를 정하지 않으면 실제 매수 주문은 나가지 않습니다. 매도는 한도에 막히지 않습니다.',
      '자동매매 기록에서 저점매수 리스크 계좌와 추천 하이리스크 계좌가 같은 이름이라 섞이던 것을 나눴습니다.',
      'Slack 알림을 관리자 채널과 공용 채널로 나눴습니다. 가격 알림 같은 개인 Slack 알림은 관리자만 켤 수 있고, 공용 채널에는 배포 소식만 갑니다.',
      '모의투자에 미국주식 화면이 생깁니다(국내주식/미국주식 스위치). 미국 저점매수 계좌는 4회차가 시작하는 날 켜지고, 그 전에는 안내만 보입니다. 합계는 원화, 가격은 달러로 보이며 환율로 생긴 몫을 따로 보여 줍니다.',
      '화면 도움말 · 알림 · AI 회고 문구 중 실제 동작과 다르게 설명하던 곳을 한꺼번에 고쳤습니다(예: 저점매수 회고가 계좌마다 다른 익절폭을 모두 +3% 로 말하던 것).',
      '관심종목 카테고리가 3개에서 더 만들어지지 않던 것을 풀었습니다.',
      '오전 9시 전에 모의투자 만료까지 남은 날이 하루 많게 보이던 것과, 매매일지에서 그날 새벽 미국장 체결이 빠지던 것을 고쳤습니다.',
      '물음표 도움말 창에서 글이 잘리고 가로 스크롤이 생기던 것을 고쳤습니다. 내용이 길면 위아래로 스크롤됩니다.',
      '삼성전자우 같은 우선주도 차트가 보입니다. 코스피 우선주 중 최근 20일 하루 평균 거래대금 10억 원 이상인 종목만 매달 골라 일봉을 모읍니다. 첫 선정은 10/10 아침입니다.',
      '분봉 차트의 시간 눈금이 9시간 늦게(오전 9시 31분 봉이 0시 31분으로) 보이던 것을 고쳤습니다. 여러 날을 볼 때 장 시작 전(8시대) 봉이 전날로 묶이던 것도 함께 고쳤습니다.',
      '회차가 끝나면 비교 탭의 순위를 그날 마감 성적표로 고정합니다(제목 "N회차 최종 성적"). 4회차부터는 보유를 다음 회차로 넘기므로 계좌 금액은 마감 뒤에도 움직이지만, 순위는 회차 이력 탭의 성적과 같게 유지됩니다.',
      '종목 차트 버튼을 봉 단위로 바꿨습니다 — 분봉 · 일봉 · 주봉 · 월봉. 처음에는 최근 구간(일봉 약 3개월 · 주봉 약 6개월 · 월봉 약 2년)이 보이고, 옆으로 밀면 과거가 나옵니다. 주봉 · 월봉은 모아 둔 일봉을 한 주 · 한 달씩 묶어 만들며, 아직 끝나지 않은 이번 주 · 이번 달 봉에는 "진행 중"이 붙습니다. (✏️ 10/16 — 이제 봉 단위 아래에 기간 버튼(3개월 · 6개월 · 1년 · 3년)이 있고, 처음에는 어느 봉이든 3개월이 보입니다.)',
      '종목/추천의 "환율" 탭이 "시장지표"로 바뀌었습니다. 안에서 환율 · 비트코인 · 금 · 국고채 · 지수(코스피 · 코스닥)를 골라 볼 수 있고, 각 지표의 최근 값과 3개월~3년 추이가 보입니다. 금 · 국고채 · 지수는 한국거래소 통계정보, 비트코인은 업비트 시세이며 하루 한 번(전 거래일 종가) 새로 모읍니다. 처음 며칠은 과거 추이가 채워지는 중이라 짧게 보일 수 있습니다.',
      '3회차(9/21)부터 한다고 알렸던 추천 계좌의 장전(08:00~08:50) NXT 매수가 실제로는 한 번도 이뤄지지 않았습니다. 종목의 NXT 지원 여부가 저장되지 않는 오류 때문이었고, 고쳤습니다. 3회차는 끝까지 9시 매수로 두고, 장전 매수는 4회차부터 합니다. 종목 팝업의 NXT 배지도 이제 보이며, 옆 물음표에서 애프터마켓과의 차이를 볼 수 있습니다.',
      '섹터 탭에서 시세가 없을 때 코스닥 종목이 KOSPI로 표시되던 것을 고쳤습니다.',
      '미국 종목 팝업에서 실시간 가격이 없을 때 "종가(시간외 포함)"로 적던 것을 "종가(정규장)"으로 바로잡았습니다. 미국 일봉은 정규장 가격만 담습니다(국내는 지금처럼 애프터마켓까지 포함한 종가입니다).',
      '미국 종목 일봉에 아직 열리지 않은 날의 봉(장 시작 전 일부 거래만 담긴 값)이 하루 동안 섞여 들어오던 것을 고쳤습니다. 이제 끝난 거래일의 봉만 저장하고, 미국 종목 팝업의 "종가(정규장)"도 확정된 값만 보여 줍니다.',
      '저점매수(챌린저)는 4회차부터 동시 보유 상한(8종목)을 없앱니다. 종목당 총자산 10%를 한 번에 쓰므로 예수금이 다할 때까지, 사실상 최대 10종목까지 삽니다. 매수 구간인데 자리가 없어 못 사는 일이 줄어드는 대신, 주식에 들어가는 돈이 늘어 시장이 빠질 때 함께 더 빠질 수 있습니다.',
      '가격 알림 탭이 위의 **국내주식 / 미국주식** 스위치를 따릅니다. 전에는 두 나라 알림이 한 목록에 섞여 있었습니다.',
      '걸어둔 알림의 **기준가를 그 자리에서 고칠 수 있습니다.** 기준가 숫자를 누르면 입력칸이 열리고, 고치면 알림이 다시 켜집니다. 저점/목표 방향은 그대로입니다.',
      '가격 알림 탭에서 **다시 걸기 · 삭제를 눌러도 목록이 튀지 않습니다.** 종목 순서는 화면을 열 때 곧 울릴 종목부터 정하고, 보는 동안에는 바뀌지 않습니다.',
      '**코스닥 종목 목록**이 다시 매일 갱신됩니다(8월 중순부터 멈춰 있었습니다).',
      '장중에 다 판 종목이 다음 날 다시 보이던 문제를 고쳤습니다.',
      '시장 기준선(KODEX 200 등) 일봉이 9월 23일에서 멈춰 있던 것을 고쳐 매일 받습니다.',
      '🔄 4회차부터 국내 추천 계좌는 **안전** 계좌만 새로 삽니다. 분할익절 · 하이리스크 계좌는 들고 있는 종목만 목표가 · 손절가 · 보유 기한에 맞춰 정리하고, 빈 자리는 곧 생길 스윙 계좌가 씁니다. 계좌 화면과 도움말에도 안내가 뜹니다.',
      '가격 알림 탭에서 **울린 알림을 한꺼번에 다시 걸거나 지울 수 있습니다.** 목록 위의 [모두 다시 걸기] · [모두 삭제]는 지금 보고 있는 나라(국내/미국)의 울린 알림에만 적용되고, 다시 걸 때 이미 기준가를 지나 바로 다시 울릴 알림이 몇 개인지 미리 알려 줍니다.',
      '주도주 탭이 거래대금 상위 **100종목**까지 보여 줍니다(전에는 50종목). 지금 주도 섹터 두 곳(반도체·AI · 전력기기·전선)에 분류된 종목은 모두 보입니다.',
    ],
  },
  {
    // 화면 퀵배포(hotfix/ui-1006 · 10/5 22:5x · 🔴 배포 창 밖 — 사용자 결정 "지금 병합해도 돼", 5e 가 창 · 미국장 개장을 먼저 알렸다)
    //   그날 main 커밋 0 + 핫픽스 커밋 1 + 병합 1 + 테스트 정정 1 = 3 (워크플로 SEQ 는 --since 오늘 0시의 전체 커밋 수) · 문안은 사용자 승인(10/5 21:5x · 22:3x)
    //   ✏️ 10/7 .2 → .3 — 2bf82b9(.2)는 CI 테스트에서 멈춰 배포되지 않았고 실제 배포는 d22b4d3(.3)이다.
    //     10/5 정정 커밋에서 이 줄을 고치는 Edit 가 실패한 것을 못 보고 커밋했다(95 오류대장 10-05) - 화면의 "지금 버전" 표시가 이 항목에 안 붙었다
    version: '2026.10.05.3',
    date: '2026-10-05',
    changes: [
      '종목 창에 **저점매수 사다리** 칸을 넣었습니다. 최근 3개월 · 6개월의 최저~최고가를 기준으로 아래쪽 30% · 20% · 10% 자리를 1단으로, 그 아래로 하루 평균 변동폭(ATR)의 1.5배씩 2단 · 3단 가격을 보여 줍니다. 줄의 [알림] 버튼을 누르면 그 가격들에 저점 알림이 한 번에 걸립니다(관심종목만 · 이미 지난 가격은 빼고). 과거 재현에서 이 자리들은 서로 가릴 수 없을 만큼 비슷해서, 추천이 아니라 계산표로 보여 줍니다.',
      '저점매수 사다리에서 현재가가 그 기간의 최저가보다 내려가면 맨 아래 줄 밑에 주황색 안내가 뜹니다.',
      '주도주 · 섹터 표에 **왕복 3개월 / 6개월** 열을 넣었습니다. 최근 몇 번, 얼마나 크게 오르내렸는지를 1년 기준으로 맞춘 값이라, 위(3개월)가 크면 최근 더 출렁인 종목입니다. 열 이름을 눌러 정렬할 수 있습니다.',
      '주도주 표의 관심 열을 뺐습니다. 관심종목 담기는 종목을 눌러 나오는 창에서 합니다. 표의 열 제목을 두 줄로 줄여 폰에서 덜 밀리게 했습니다.',
      '저점매수 보유 종목의 매수 1차 · 2차를 청록 · 보라 표시로 구분했습니다.',
    ],
  },
  {
    // 그날 main 커밋 8 + 이 커밋 1 + 병합 1 = 10 (워크플로 SEQ 는 --since 오늘 0시의 전체 커밋 수)
    version: '2026.09.24.10',
    date: '2026-09-24',
    changes: [
      '매매 회고(건별)가 **"이 거래가 왜 그렇게 끝났나" 까지만** 쓰도록 바꿨습니다. 전에는 한 건을 보고 "그러니 이런 진입은 막아야 한다" 로 나아가는 문장이 섞였는데, 그렇게 제안된 조건을 계좌 전체로 다시 굴려 보니 나쁜 거래와 함께 좋은 거래도 걸러져 **오히려 성적이 나아지지 않았습니다.** 그 판단은 회고가 아니라 계좌 단위 측정이 합니다.',
      '회차 종합 분석이 **회차가 끝나서 판 손익을 목표·손절로 끝난 거래와 따로** 말하도록 바꿨습니다. 회차 종료 청산은 실험의 구조라 실제 계좌에는 없는데, 저점매수 두 계좌에서 그 손실(33건 · −125만 원)이 손절 전체(−109만 원)보다 컸습니다. 섞어 읽으면 매매 규칙이 실제보다 나빠 보입니다.',
      '회차 종합 분석의 "재 볼 만한 것" 이 **"이런 진입을 거르면 계좌 전체가 나아지나"** 처럼 계좌 전체에 대한 질문으로 나오게 했습니다.',
    ],
  },
  {
    // 병합(ed7d90e) 뒤 그날 main 커밋 4 + 이 커밋 1 = 5 (2026-09-21 f810ff2 과 같은 방식)
    version: '2026.09.23.5',
    date: '2026-09-23',
    changes: [
      '🔴 저점매수의 **매수 기준가가 매일 오후 3시 41분에 아직 확정되지 않은 값으로 다시 계산되고 있었습니다.** 기준가는 "최근 120일 종가 중 낮은 쪽 20%(또는 40%) 자리" 인데, 그 계산에 들어가는 **오늘 종가가 잠정값**이었습니다. 토스증권의 하루치 값은 애프터마켓(저녁 8시)까지 합친 것이라, 오후 3시 40분에 받는 값은 확정이 아니고 저녁 8시 25분에 다시 받아야 확정됩니다. 지난번에 재보니 코스피 804종목 중 **653개의 종가가 나중에 바뀌었고 평균 0.78% · 최대 6.42%** 움직였습니다.',
      '왜 생겼나 — 9월 19일에 두 가지를 같은 날 바꿨습니다. ① 감시를 저녁 8시까지로 늘렸고 ② 기준가 계산을 오후 4시에서 **저녁 9시(확정값 뒤)** 로 옮겼습니다. 각각은 맞는데, 겹쳐 놓으면 **오후 3시 41분 감시 주기가 방금 들어온 잠정값을 보고** 기준가를 다시 만들어 버립니다. 그리고 저녁 9시 배치는 "오늘 것이 이미 있네" 하고 그냥 넘어갔습니다. 화면의 설명("감시 대상은 평일 21:00에 다시 뽑습니다")이 의도는 맞았지만 실제와 달랐던 것입니다.',
      '고친 방법 둘 — ① 장중에는 **오늘 값이 확정되기 전까지(저녁 8시 30분) 아예 쓰지 않습니다.** 어제까지의 확정값으로 만든 기준가를 하루 종일 그대로 씁니다. ② 저녁 9시 배치는 **오늘 것이 이미 있어도 확정값으로 다시 만듭니다.** ①만으로도 막히지만, "저녁 9시를 지나면 기준가는 반드시 확정값 위에 있다"를 날짜 비교에 기대지 않으려고 둘 다 넣었습니다.',
      '🔴 **이것은 회차 중 규칙 변경이 아닙니다.** 9월 19일에 이바다님이 고른 규칙("확정값으로 기준가를 만든다")이 실제로는 그렇게 돌지 않고 있었고, 그것을 원래 의도대로 되돌린 것입니다.',
      '영향 범위 — 잠정값 기준가로 **새로 산 것**은 9월 21일 오후 챌린저 8건, 9월 22일 오후 챌린저 2건, 9월 23일 하루 6건입니다(배포가 저녁 9시 40분이라 오늘은 오염된 채로 끝납니다). 한 번 산 종목은 살 때의 기준가·손절선을 끝까지 쓰므로, 며칠 뒤 팔리더라도 그 종목은 오염된 조건에서 산 것입니다. 계좌 성적에서 빼지는 않고 표시만 해 둡니다 — 실제로 그 가격에 샀고 그 손익이 났기 때문입니다. 대신 **백테스트와 실제를 대조할 때는 제외**합니다.',
      '고치면서 같이 찾은 것 — "같은 날 기준가를 다시 만드는" 경로가 **데이터베이스 제약에 걸려 실패하는 상태**였습니다. 지우기보다 넣기가 먼저 실행되는 순서 문제입니다. 지금까지 그 경로가 한 번도 실제로 돈 적이 없어 드러나지 않았는데, 이번 수정이 처음으로 그 길을 지나가게 만들어서 함께 고쳤습니다.',
      '테스트를 9개 넣고, **수정을 일부러 되돌려 6개가 실패하는지 확인**했습니다. 특히 "저녁 9시 배치가 강제 재계산을 부르는가" 를 따로 검사합니다 — 9월 21일 그림자 기록 결함이 "각 부품은 멀쩡한데 잇는 배선만 아무도 안 봤다" 였기 때문입니다.',
    ],
  },
  {
    // 병합(3ac3b54) 뒤 그날 main 커밋 9 + 이 커밋 1 = 10 (2026-09-20 8df14f0 과 같은 방식)
    version: '2026.09.21.10',
    date: '2026-09-21',
    changes: [
      '🔴 그림자 모드가 **첫 매수를 기록하지 않는 결함**이 있었습니다. 어제 저녁 켜고 오늘 아침 8시에 저점매수가 여러 종목을 샀는데 기록에는 2건만 남아, 이바다님이 화면을 보고 물어서 찾았습니다. 저점매수는 한 종목을 여러 번에 걸쳐 나눠 사는데(1차 → 2차 → 3차), 기록을 붙인 곳이 **2차 이후뿐**이었습니다. 실제로는 매수의 85%가 1차라서 그림자가 **실제 매매의 15%만** 남기고 있었습니다.',
      '고친 방법 — 매수 경로가 둘이라 한쪽을 빼먹을 수 있었으니, **두 경로가 반드시 같은 한 곳을 지나가도록** 바꿨습니다. 그리고 "경로마다 기록이 남는가"를 검사하는 테스트를 8개 넣었습니다. 기록을 일부러 지워서 그 테스트가 정말 실패하는지도 확인했습니다 — 통과하는 테스트만 보면 아무것도 확인한 게 아니기 때문입니다.',
      '같이 찾은 것 — 추천 계좌도 **회차가 끝나 전부 정리할 때**의 매도가 기록되지 않았습니다. 그 매도는 알림을 일부러 보내지 않는데(한꺼번에 팔려 알림이 도배됩니다), 기록이 알림과 한 곳에 묶여 있어 같이 빠졌습니다. 이제 알림은 빼고 기록만 남깁니다.',
      '🔴 **놓친 기록을 나중에 채워 넣지는 않았습니다.** 채우면 "그때 실제로 그 판단을 했다"가 아니라 "나중에 그랬을 것이라고 적었다"가 됩니다. 그림자 기록의 값은 바로 그 시점성에 있어서, 한 번 섞으면 남은 기록 전체를 믿기 어려워집니다. 대신 모의투자와의 **대조는 9월 22일부터** 하고, 9월 21일은 불완전한 날로 표시해 뒀습니다.',
      '회차가 끝나 전부 정리할 때의 매도가 **시각 없이** 저장되고 있었습니다. 그래서 거래 기록표에는 실제 정리 시각(저녁 8시 40분) 대신 오후 3시 30분으로 찍혔습니다. 이제 실제 시각이 남습니다. 지난 2회차의 정리 27건은 그대로 두었습니다 — 몇 시에 팔았는지 기록이 없어서, 채우면 추정이 됩니다.',
      '저점매수가 살 때마다 **"사다리 간격이 달랐다면"** 을 뒤에서 따라가는 기록을 시작합니다(내일 아침부터). 지금은 한 종목을 1차로 사고 1 ATR 더 빠지면 2차를 사는데, 그 간격이 0.5 · 0.75 ATR 이었다면 2·3차가 체결됐을지, 손익은 어땠을지를 가상으로 계산해 남깁니다. 돈은 움직이지 않고 매매 규칙도 그대로입니다. 1 ATR(지금과 같은 간격)도 함께 따라가서, 그 가상 기록이 실제 매매와 똑같이 나오는지로 계산이 맞는지 스스로 확인합니다.',
      '위를 설계하다 알게 된 것 — 지금 설정에서는 **사다리 3차가 절대 체결될 수 없습니다.** 3차를 살 가격까지 내려가면 그 전에 이미 손절선을 지나 먼저 팔리기 때문입니다. 실제로 지금까지 3차는 한 번도 없었습니다. 그래서 "종목당 20%" 는 실제로는 최대 13.3% 까지만 쓰입니다. 규칙을 바꾸자는 것이 아니라, 다음 회차에 규칙을 고를 때 이 뜻을 알고 고르기 위해 적어 둡니다.',
      '배운 것 하나 — 기록이 **0건이 아니라 2건**이어서 겉보기에는 잘 돌아가고 있었습니다. 아예 0건이면 아침에 바로 알아챘을 것입니다. 부분적으로 도는 고장이 완전히 멈춘 고장보다 발견이 늦습니다. 앞으로 새 기록 장치를 켤 때는 "기록이 있나"가 아니라 **"있어야 할 수와 같나"**를 확인합니다.',
    ],
  },
  {
    version: '2026.09.20.22',
    date: '2026-09-20',
    changes: [
      '[계좌]에 "자동매매" 탭이 생겼습니다. 지금은 **보기만** 하는 화면입니다 — 모드는 꺼져 있고, 켜는 버튼은 아직 없습니다. 모드를 올리려면 지문·Face ID 같은 추가 인증이 필요한데 그 기능을 아직 만들지 않았습니다. 인증 없이 켜는 길을 먼저 만들면 나중에 인증을 붙여도 그 우회로가 남기 때문입니다.',
      '자동매매에 "그림자 모드"를 준비했습니다. **주문을 내지 않고**, 모의투자 계좌가 사고팔 때 "내 계좌라면 이런 주문이 나갔을 것"만 기록합니다. 자동매매의 진짜 위험은 규칙이 틀린 것이 아니라 생각한 대로 코드가 안 도는 것인데, 그림자 모드는 그것을 돈을 걸지 않고 확인합니다. 모의 계좌가 산 것과 그림자가 내려 한 것이 며칠 일치하면 그때 다음 단계로 갑니다.',
      '계좌·보유·체결이 **화면을 보고 있는 동안 1분마다** 새로 읽힙니다. 전에는 평일 오후 4시에 하루 한 번만 받아와서, 그 뒤 애프터마켓(16~20시) 체결과 미국 시장 체결이 다음 날까지 보이지 않았습니다. 미국은 최대 24시간 늦었습니다. 화면을 닫아두면 받아오지 않으므로 평소 부하는 늘지 않습니다. 저녁 8시 10분(국내 마감 뒤)과 새벽 6시 10분(미국 마감 뒤)에도 한 번씩 확정 기록을 남깁니다.',
      '종목 차트 위에 그 봉의 값이 한 줄로 나옵니다 — 날짜 · 시가 · 고가 · 저가 · 종가 · 거래량. 차트 위에 손가락(마우스)을 올리면 그 자리의 봉을 따라가고, 떼면 마지막 봉으로 돌아옵니다. 전에는 차트 오른쪽 아래에 16,971,358 같은 수가 설명 없이 떠 있었는데, 그것은 거래량인데 가격과 같은 축에 찍혀 가격처럼 보였습니다. 그 표시는 없애고 이름과 함께 위로 옮겼습니다.',
      '차트에서 날짜를 짚으면 "10 8월 \'26" 이 아니라 "2026년 8월 10일" 로 나옵니다.',
      '미국주식 일봉을 매일 아침 10시 30분에 받아옵니다. 전에는 한 번 받아둔 9월 18일치에서 멈춰 있었습니다. 10시 30분인 것은 미국 시간외 거래가 우리 시간으로 8시 50분에 끝나기 때문입니다 — 그 전에 받으면 아직 확정되지 않은 값이 들어옵니다.',
      '🔴 단타 화면 전체의 설명을 훑어 틀린 곳을 고쳤습니다. 사용자 요청으로 국내·미국 모든 탭의 표·도움말·안내 문구를 실제 코드·설정과 하나씩 대조했습니다. 고친 것 중 중요한 것 — ① 미국 종목 상세가 "정규장(09:00~15:30)과 시간외(~20:00)"라고 국내 장 시간을 말하고 있었습니다. 미국 일자는 **미국 현지(ET) 날짜**라는 것도 밝혔습니다. ② 섹터·주도주 화면에서 코스닥 종목에 "선별 대상" 배지가 붙고 있었습니다 — 자동 선별은 코스피만 대상인데, 코스닥 종목의 오래된 표시가 그대로 남아 있었습니다. ③ 주도주 빈 화면이 "관리자 화면에서 섹터를 지정하면"이라고 안내했는데 그런 화면이 없습니다. ④ 섹터 분류가 **전 종목을 덮지 않는다**는 사실을 적었습니다 — 손으로 넣은 목록이라 분류되지 않은 종목은 어느 테마에도 나오지 않습니다. ⑤ 계좌 화면이 아직 "평일 16:00 배치가 하루 한 번"이라고 말하고 있었습니다(바로 위 항목에서 바뀐 내용입니다).',
      '회차 종합 분석을 쓰는 AI에게 주는 설명도 고쳤습니다. "다섯 계좌 · 감시 50종목 · 저점매수 둘의 차이는 매수 깊이 하나뿐"이라고 알려주고 있었는데, 지금은 여섯 계좌 · 100종목이고 저점매수 셋은 깊이 · 익절폭 · 손절폭 · 분할 단수가 모두 다릅니다. 틀린 전제로 쓴 회고가 그대로 화면에 나가고 있었습니다.',
    ],
  },
  {
    version: '2026.09.20.9',
    date: '2026-09-20',
    changes: [
      '[환율] 추이에 3개월 · 6개월 · 1년 · 3년 버튼이 생겼습니다. 토스증권은 지금 환율만 주고 우리가 쌓은 기록도 8월 20일부터라, 그 앞 구간은 한국은행이 고시하는 일별 매매기준율로 채웠습니다. 성격이 조금 다른 값이라 그 구간을 보고 있으면 차트 아래에 표시됩니다. 30일이 넘는 기간은 하루에 한 점(그날 마지막 값)으로 그립니다 — 1년을 한 시간 간격으로 그리면 8,760점이라 선이 뭉개집니다.',
      '🔴 시세를 받아오지 못할 때 관심종목 · 추천 목록이 오류를 내던 것을 고쳤습니다. 이제 마지막 종가를 "(종가)" 표시와 함께 보여줍니다. 9월 20일 오전 토스증권 점검 중에 화면이 15초마다 시세를 물어보면서 같은 오류가 922건 쌓였습니다 — 관리자 오류 목록이 한 종류로 도배되면 정작 봐야 할 오류가 묻힙니다. 같은 오류는 5분에 한 번만 기록하도록 함께 고쳤습니다(서버 로그에는 모두 남습니다).',
      '종목 상세의 "종가"가 정규장 종가가 아니라는 것을 밝혔습니다. 우리 일봉은 정규장(09:00~15:30)과 시간외(~20:00)를 합친 값이라, 증권사나 포털이 대표로 보여주는 정규장 종가와 몇백 원 다를 수 있습니다. 9월 19일에 일부러 그렇게 바꿨는데(시간외까지 반영된 값이 실제 매매에 더 가깝습니다) 화면이 그 사실을 말하지 않아 틀린 숫자처럼 보였습니다.',
    ],
  },
  {
    version: '2026.09.20.7',
    date: '2026-09-20',
    changes: [
      '🔴 시세를 받아오지 못할 때 [저점매수] 탭이 통째로 열리지 않던 것을 고쳤습니다. 이제 현재가 칸만 비고 보유 수량 · 평단 · 청산 기록 · 회고는 그대로 보입니다. 9월 20일 아침 토스증권이 점검 중이라 현재가를 한 번 못 받았는데, 그 한 번 때문에 탭 전체가 오류가 됐습니다. [환율] 탭은 같은 상황에서 저장된 마지막 값으로 대신하며 정상으로 떴습니다 — 화면은 이쪽이 맞습니다. 매매 · 알림 · 회차 정리는 반대로 그대로 실패하게 두었습니다. 시세 없이 조용히 아무 매매도 안 하고 끝나면 하루를 잃고도 모르기 때문입니다(그때는 Slack 으로 알립니다).',
      '🔴 미국주식을 고른 채 [AI 모의투자]나 [지난 추천]을 열면 "준비 중" 안내 아래에 국내 계좌 6개와 국내 추천 이력이 그대로 보이던 것을 고쳤습니다. 이제 준비 중 안내만 보입니다. 미국 계좌는 아직 만들지 않았습니다 — 계좌를 국가별로 나누는 작업이 필요합니다.',
      '"해외주식"을 "미국주식"으로 바꿨습니다. 지원하는 해외 시장이 미국(나스닥 · 뉴욕 · 아멕스)뿐이라 "해외"는 실제보다 넓게 말하고 있었습니다. 시장 스위치에 작은 국기도 붙였습니다.',
      '가격 알림 · 환율처럼 두 시장이 함께 쓰는 화면에서도 국내 ↔ 미국 전환이 됩니다. 전에는 눌리지 않아서, 환율을 보다가 미국으로 넘어가려면 다른 탭을 먼저 눌러야 했습니다. 전환해도 그 화면 내용은 같고 아래 탭 구성만 바뀝니다.',
      '[환율]에서 "달러값이 올랐습니다"에 색이 붙습니다. 오르면 빨강 · 내리면 파랑으로, 시세 화면과 같은 규칙입니다. 추이 그래프 선 색은 그대로 둡니다 — 선 하나뿐이라 빨간 선이 "지금 오른 상태"로 잘못 읽히기 때문입니다.',
      '비교 차트 아래 규칙 변경 설명에서 "다음 재선정(월요일 16:05)" 이라고 적혀 있던 것을 고쳤습니다. 9월 12일 당시에는 16:05 였지만 9월 19일부터 감시 대상 재계산은 21:00 입니다.',
    ],
  },
  {
    version: '2026.09.20.3',
    date: '2026-09-20',
    changes: [
      '단기매매 맨 끝에 [환율] 탭이 생겼습니다. 지금 1달러가 몇 원인지(1분마다 새로 읽음), 달러 ↔ 원 계산기, 최근 7일 · 30일 환율 추이를 봅니다. 토스증권의 참고 환율이라 실제 환전 환율과는 조금 다를 수 있습니다.',
      '3회차(9/21)부터 저점매수(챌린저)는 감시 종목 중 왕복성 상위 30종목만 새로 삽니다. 자리가 8개뿐이라 순위 낮은 종목이 먼저 걸려 자리를 차지하면 성적이 크게 갈렸습니다(과거 2년 측정: 검증 구간 수익 +101% → +132%, 최대 낙폭 −33% → −20%).',
      '3회차(9/21)부터 저점매수 세 계좌는 가격이 이미 손절선 아래로 내려와 있으면 사지 않습니다. 전에는 사자마자 다음 분에 손절하고, 한 달 동안 그 종목을 다시 사지 못했습니다.',
      '3회차(9/21)부터 저점매수의 손절선이 매수 기준가 − 2 ATR에서 − 1.5 ATR로 올라갑니다(1.5%가 아니라 그 종목 하루 평균 변동폭의 1.5배). 2회차 회고에서 "회차 끝에 손실로 물려 있는 종목"을 줄이는 방법을 과거 2년으로 재보니, 이 계좌는 손절을 당겼을 때 앞 · 뒤 기간 모두 수익이 오르고 계좌가 출렁이는 폭이 줄었습니다. 대신 손절이 약 25% 늘어납니다.',
      '3회차(9/21)부터 저점매수(리스크)는 +4%가 아니라 +5%에 팝니다. 예전 검증이 "산 날에는 팔지 못한다"는 가정으로 재져 좁은 익절이 유리하게 보였고, 그 가정을 고쳐 다시 재니 +5%가 네 경우 중 셋에서 나았고 하나는 같았습니다. 저점매수(챌린저)는 +4% 그대로입니다.',
      '3회차(9/21)부터 추천 계좌(안전 · 분할익절 · 하이리스크)는 전날 밤 추천 중 NXT(넥스트레이드)에서도 거래되는 종목을 장전 08:00~08:50부터 삽니다. 나머지 종목은 전처럼 09:00부터입니다. 장전에 산 거래에는 "장전 · NXT" 표시가 붙습니다. (✏️ 9/29 정정 — 오류로 3회차에는 장전에 사지 않았습니다. 4회차부터 합니다. 4회차부터 새로 사는 추천 계좌는 안전 하나입니다.)',
      '3회차(9/21)부터 추천 계좌는 목표가 · 손절가를 정규장이 끝난 뒤 시간외(20:00까지)에도 봅니다. 전에는 15:30 뒤에 손절선을 뚫어도 다음 날 아침까지 팔지 않았습니다. 15거래일이 지나 정리하는 시각도 15:10에서 저녁 19:50으로 옮겼습니다. 사는 것은 전처럼 정규장(NXT 종목은 장전 포함)에만 합니다.',
      '추천 계좌 도움말에서 익절 · 손절선이 "매수가" 기준이라고 적혀 있던 것을 "추천가(추천 당일 종가)" 기준으로 바로잡았습니다. 실제 매매는 처음부터 추천가 기준이었습니다 — 추천가보다 비싸게 사면 그만큼 목표는 멀고 손절은 가깝습니다.',
      '국내주식 / 해외주식 전환을 단기매매 하위 탭 위로 옮기고 스위치 모양으로 바꿨습니다. 시장을 먼저 고르고 그 안에서 화면을 고릅니다. 해외를 고르면 국내에만 있는 주도주 · 섹터 탭은 숨고, 가격 알림 · 환율처럼 두 시장이 함께 쓰는 화면에서는 스위치가 흐려지며 "공통"이라고 표시됩니다. (지금은 그 화면에서도 전환이 됩니다 — 같은 날 2026.09.20.7 에서 되돌렸습니다.)',
      '저점매수 감시 대상 표 위에 [매수 순서(예상)]이 생겼습니다. 지금 매수 구간인 종목을 왕복성 순위 순으로 맨 앞에, 그다음은 매수 기준가에 가까운 순으로 세워 실제로 살 순서를 미리 봅니다. 기준가 아래여도 사지 않는 종목에는 "손절선 아래 · 안 삼", "순위 밖 · 안 삼", "보유 중"이 붙습니다.',
    ],
  },
  {
    version: '2026.09.19.11',
    date: '2026-09-19',
    changes: [
      '종목 이름을 누르면 어느 화면이든 종목 팝업이 뜹니다. [AI 모의투자]의 보유 · 청산 완료 · 매매 이력, [계좌]의 보유 종목 · 주문 내역, 내 매매 기록이 새로 눌립니다.',
      '저점매수(챌린저) 화면이 설정을 "최대 3번 나눠서 · 종목당 20%"로 잘못 보여주던 것을 고쳤습니다. 실제 설정(한 번에 · 종목당 10% · 최대 8종목)대로 보이고, 챌린저에는 "다음 매수" 가격이 뜨지 않습니다. 매매 자체는 처음부터 설정대로 돌았습니다.',
      '청산 목록에서 회차 마지막 날 한꺼번에 정리한 거래가 빨간 "구조 이탈"로 보이던 것을 회색 "회차 종료"로 바꿨습니다. 비교 탭 매도 사유에도 "회차 종료" 칸이 생겼고, 이 거래들에는 "회고 대기"가 붙지 않습니다.',
      '저점매수 매수 요약 Slack이 15:45에서 20:05로 옮겨졌습니다. 매매가 20시까지 돌면서 오후 늦게 산 것이 요약에서 빠질 수 있었습니다.',
      '비교 차트에서 저점매수(챌린저) 선을 일점쇄선(긴 선 · 짧은 선)으로 바꿨습니다. 시장 기준선과 모양이 같았습니다.',
      '도움말 문구를 실제 값에 맞췄습니다 — 레인지 위치(0~100), 변동성(ATR) 기준의 근거, 과거 도달률의 표본 안내, 추천이 나오는 시각(평일 밤 9시 5분), 만료 정리 시점, 챌린저 설명 등.',
    ],
  },
  {
    version: '2026.09.19.9',
    date: '2026-09-19',
    changes: [
      '[AI 모의투자] 비교 · 회차 이력 탭이 열리지 않던 오류를 고쳤습니다(9/19 오전부터). 새 계좌 정보가 화면보다 먼저 DB에 들어가서 생긴 문제였습니다.',
      '저점매수(챌린저) 계좌가 생겼습니다. 3회차(9/21)부터 굴러갑니다. 저점매수(리스크)와 같은 규칙에서 "나눠 사기" 대신 종목당 총자산 10%를 한 번에 사고, 최대 8종목까지만 들고 갑니다. 비교 차트에는 청록 점선으로 그려집니다.',
      '저점매수 세 계좌가 08:00~20:00에 사고팝니다(전에는 09:00~15:30). 한국거래소 애프터마켓(16:00~20:00)이 생긴 데 맞췄습니다.',
      '저녁 시각표가 바뀌었습니다. 애프터마켓이 끝난 뒤의 확정 가격으로 계산하도록 감시 대상은 21:00, 추천은 21:05, 회고·회차 종합 분석은 21:30에 나옵니다.',
      '종목 팝업에 NXT 배지가 붙습니다. 넥스트레이드(대체거래소)에서도 거래되는 종목이라는 뜻입니다.',
      '국내/해외 선택을 추천 · AI 모의투자 · 주도주 · 섹터 · 지난 추천 다섯 화면이 함께 기억합니다. 해외는 아직 준비 중 안내가 뜹니다.',
      '회차가 끝날 때 한꺼번에 정리한 거래는 AI 회고를 쓰지 않습니다(판 이유가 매매 판단이 아니라 기간이라서). 회차 종합 분석은 다른 회고보다 먼저 씁니다.',
      '화면 곳곳의 안내 시각을 새 시각표에 맞췄습니다.',
    ],
  },
  {
    version: '2026.09.16.6',
    date: '2026-09-16',
    changes: [
      '시세가 15초마다 갱신됩니다(전에는 30초). 관심종목 · 주도주 · 섹터 · 가격 알림 · 저점매수 감시 대상의 현재가가 모두 해당하고, 가격 알림 검사도 15초마다 합니다.',
      '저점매수 보유 종목의 현재가 · 평가액 · 수익률(%)이 15초 시세로 계산됩니다. 전에는 2분마다 바뀌어 바로 위 전날 대비와 어긋날 수 있었습니다. [수익률] 정렬도 같은 값을 씁니다. 계좌 카드(총자산 등)는 그대로 2분마다 갱신됩니다.',
    ],
  },
  {
    version: '2026.09.16.5',
    date: '2026-09-16',
    changes: [
      '저점매수 보유 종목 칸의 줄을 나눴습니다. 2차 이상을 산 종목은 매수 기록이 차수마다 한 줄씩(최근 매수가 위) 나오고, 그 아래에 현재 · 평가액 한 줄, 다음 줄에 목표 · 다음 매수 · 손절 순서로 나옵니다.',
      '저점매수 [보유 N종목] 옆 도움말 버튼이 돋보기에서 다른 화면과 같은 물음표(?)로 바뀌었습니다.',
    ],
  },
  {
    version: '2026.09.16.4',
    date: '2026-09-16',
    changes: [
      '저점매수 보유 종목의 [사다리 보기] / [접기] 가 없어졌습니다. 2차 이상을 산 종목은 매수 줄에 차수별 시각과 가격 × 수량이 최근 매수부터 함께 나옵니다. 같은 정보가 두 곳에 나오지 않게 합쳤습니다.',
    ],
  },
  {
    version: '2026.09.16.3',
    date: '2026-09-16',
    changes: [
      '안전 · 분할익절 · 하이리스크 추천 알림이 공용 채널이 아니라 각 AI 계좌의 Slack 채널로 갑니다. 그 계좌의 매수·매도·회고와 같은 곳에서 봅니다.',
      '저점매수 [보유 N종목] 옆 🔍 를 누르면 칸의 숫자(평단·전날 대비·목표·손절·다음 매수 등)가 무엇인지 설명이 나옵니다. 익절폭·손절 배수는 지금 설정값으로 적힙니다.',
      '저점매수 보유 종목에서 2차 이상을 산 경우, 매수 줄에 차수별 매수 시각이 최근 것부터 함께 나옵니다. 사다리 목록도 최근 매수가 위로 오고, 2단 이상이면 처음부터 펼쳐져 있습니다.',
      '저점매수 감시 대상 표에서 폰일 때 종목 칸 폭을 줄였습니다. 이름이 긴 종목은 두 줄로 접혀 표가 가로로 밀리지 않습니다.',
    ],
  },
  {
    version: '2026.09.16.2',
    date: '2026-09-16',
    changes: [
      '저점매수 [보유]에서 전날 대비 줄이 평단 × 주수 = 합계 바로 아래로 올라왔습니다. 수익률과 오늘 등락을 위쪽에서 함께 봅니다.',
      '저점매수 [보유]의 [사다리 보기] / [접기] 버튼이 종목 칸 맨 아래 줄로 내려갔습니다. 펼친 사다리 끝에서 바로 접을 수 있습니다.',
    ],
  },
  {
    version: '2026.09.16.1',
    date: '2026-09-16',
    changes: [
      '저점매수 두 계좌의 [청산]이 최근 30건이 아니라 이번 회차 청산 전부를 보여줍니다. 10건씩 페이지로 넘깁니다.',
      '저점매수 [보유] 종목마다 전날 종가 대비 등락(한 주당 금액·%)과 보유 수량만큼의 금액이 함께 나옵니다. 평단 대비 수익률과 따로 봅니다.',
      '저점매수 [보유]에 정렬 버튼이 생겼습니다. 기본은 매수일 최신순이며 2차를 산 종목은 2차 매수 시각이 기준입니다. 한 번 더 누르면 오래된순, [수익률]은 높은순·낮은순입니다.',
    ],
  },
  {
    version: '2026.09.14.2',
    date: '2026-09-14',
    changes: [
      '관심종목·주도주·섹터 표의 [대비 / 등락률]이 PC에서도 두 줄로 나옵니다. 위가 대비, 아래가 등락률로 열 제목과 같은 순서입니다. 전에는 폰에서만 폭이 좁아 두 줄로 넘어갔고 PC에서는 한 줄로 붙어 있었습니다.',
    ],
  },
  {
    version: '2026.09.14.1',
    date: '2026-09-14',
    changes: [
      '저점매수 두 계좌 도움말에 [재봤지만 넣지 않은 것] 절이 생겼습니다. 코스닥 종목도 감시하기, 갭하락으로 시작한 날 사지 않기를 과거 2년치로 재봤는데 둘 다 수익은 줄고 낙폭은 커져 넣지 않았습니다. 특히 갭하락으로 시작한 날이 오히려 가장 싸게 사는 날이었습니다. 설정은 그대로입니다.',
    ],
  },
  {
    version: '2026.09.13.1',
    date: '2026-09-13',
    changes: [
      '🔴 분할익절 도움말의 [왜 이 계좌를 먼저 참고하라고 하나]를 정정했습니다. 어제 적은 근거("검증에서 추천 3계좌 중 유일하게 플러스 +21.3%")는 업종 필터를 켜기 전 숫자였습니다. 지금 설정으로는 셋 다 플러스이고, 분할익절은 같은 기간 시장보다 9.4%p 낮았습니다. 추천 3계좌끼리만 견줬던 것도 문제였습니다 — 다섯 계좌를 같은 잣대로 놓으면 저점매수 두 계좌가 앞뒤 두 기간 모두 시장을 크게 이기면서 낙폭은 절반 수준입니다. 비교 탭 도움말에 그 비교를 넣었습니다.',
      '저점매수 두 계좌의 [근거] 절을 지금 설정(감시 100종목·체결 손해 0.2% 포함)으로 다시 잰 숫자로 바꿨습니다. 이전 문구의 "저점매수는 앞 기간에 시장보다 7.2%p 뒤졌다"는 감시 50종목 시절 숫자라 지금은 맞지 않습니다.',
    ],
  },
  {
    version: '2026.09.12.2',
    date: '2026-09-12',
    changes: [
      '🔴 저점매수 두 화면의 순서가 바뀌었습니다. [계좌 → 보유 → 청산 → 회고 → 감시 대상]입니다. 감시 대상을 맨 아래로 내리면서 표 안의 세로 스크롤을 없앴습니다 — 페이지 스크롤 안에 또 스크롤이 있으면 손가락이 어디로 가야 할지 알 수 없고, 열이 넷이라 가로로도 밀렸습니다. 이제 50종목이든 100종목이든 전부 펼쳐집니다.',
      '저점매수 감시 대상 표의 [매수 기준가]와 [기준가까지]가 한 칸에 두 줄로 합쳐졌습니다. 열이 셋이 되어 폰에서 가로 스크롤이 사라집니다. 두 이름은 여전히 각각 눌러서 정렬할 수 있습니다.',
      '저점매수 청산 목록에 페이지가 생겼습니다(10건씩). 다섯 영역 중 청산만 회차를 거듭할수록 끝없이 늘어나서입니다 — 보유는 종목당 예산이 개수를 묶어두고 감시 대상은 설정값으로 고정입니다. 추천 3계좌가 이미 같은 방식이라 두 계열의 조작이 같아졌습니다.',
      '🔴 주도주·섹터·관심종목의 [대비 · 등락률]이 [대비 / 등락률]로 바뀌고 값이 두 줄로 쌓입니다. 주 사용자가 모바일이라 가로 스크롤을 없애는 쪽을 택했습니다. 대비(금액)와 등락률(%)은 각각 눌러서 정렬할 수 있습니다 — 정렬 결과가 다릅니다. 10만원짜리가 +1,000원(+1%) 오른 것과 1만원짜리가 +500원(+5%) 오른 것의 순서가 뒤집힙니다.',
      '섹터 화면에서 [시가총액] 열이 빠지고 종목을 누르면 나오는 창으로 옮겨갔습니다. 목록에서 매번 볼 숫자가 아니라 종목을 알아볼 때 한 번 보는 숫자이고, 열을 줄여야 폰에서 표가 밀리지 않습니다. 목록의 기본 순서는 그대로 시가총액이고 종목명 앞의 번호가 그 순위입니다.',
    ],
  },
  {
    version: '2026.09.12.1',
    date: '2026-09-12',
    changes: [
      '🔴 AI 모의투자 5계좌가 각자의 Slack 채널로 매수·매도·회고를 보냅니다. 지금까지 이 계좌들은 Slack 알림이 아예 없었습니다 — 채널을 나눈 것이 아니라 알림을 새로 만든 것입니다. 추천 카드·배치 실패·가격 알림은 원래대로 공용 채널입니다(추천 3계좌는 같은 종목을 사기 때문에 계좌 채널로 보내면 같은 카드가 세 번 옵니다). 저점매수 두 계좌는 하루에 5~15건씩 사서, 매수는 장 마감 뒤 요약 한 통으로 묶고 매도만 건건이 보냅니다.',
      '🔴 계좌 카드가 [총자산 = 시드 + 실현 + 평가] 수식을 보여줍니다. 그 아래에 [지금 다 팔면 얼마인지](수수료·거래세를 뺀 값)와 [승률 옆의 미실현]이 함께 나옵니다. 승률만 떨어져 있으면 오해를 부르기 때문입니다 — 저점매수(리스크)는 승률이 82%인데 아직 안 판 종목이 −53만원입니다. 오른 종목은 +4%에 팔리고 안 오른 종목은 남는 규칙이라 그렇게 됩니다.',
      '계좌 카드에 예수금 비중과 최대 낙폭이 붙었습니다. 낙폭은 비교 탭에만 있던 값인데, 수익률보다 이쪽이 기간이 바뀌어도 유지되는 성질이라 계좌마다 보이게 했습니다. 예수금 비중이 10% 미만이면 표시가 붙습니다 — 경고가 아니라 사다리 추가 매수를 받을 여력이 얼마인지 보여주는 것입니다.',
      '보유 종목에 [N일째]가 나옵니다. 며칠째 안 팔리고 있는지가 한눈에 보입니다.',
      '손익·실현 손익·평가 손익 설명이 표 제목 옆 물음표와 계좌 도움말에 들어갔습니다. 예시로 설명합니다 — 시드 2,000만에 실현 +140만, 평가 −53만이면 총자산은 2,087만이고, 지금 다 팔면 손에 남는 것은 +87만입니다.',
      '🔴 저점매수 감시 대상이 50종목에서 100종목으로 늘어납니다. 2년치를 앞뒤로 갈라 다시 재보니 두 계좌 모두 앞 기간·뒤 기간 수익이 함께 올랐습니다(저점매수 뒤 기간 +81.8% → +159.5%). 계좌가 출렁이는 폭은 ±2%p 안에서 좋아지기도 나빠지기도 해 채택 근거로 쓰지 않았습니다. 다음 갱신(평일 16:05)부터 반영되고, 목록이 길어져 표 안에서 스크롤됩니다(제목 줄은 고정).',
      '비교 차트에 규칙이 바뀐 날이 세로 점선으로 표시됩니다. 지금은 두 개입니다 — 9/10 저점매수(리스크) 익절 +3%→+4%, 9/12 감시 대상 50→100. 회차 도중에 규칙이 바뀌면 그 앞뒤 성적을 한 규칙의 것으로 읽을 수 없어서 선을 긋습니다.',
      '주도주 탭에 🔥 아이콘과 주황색이 붙어 다른 탭과 구분됩니다. 색만 쓰지 않고 아이콘을 함께 둔 것은 색각 이상이나 흑백 화면에서도 갈리게 하려는 것입니다. 다크 모드에서는 글자색까지 바꿨습니다 — 주황을 밝히면 흰 글자의 대비가 무너집니다.',
      '분할익절 계좌 도움말에 [왜 이 계좌를 먼저 참고하라고 하나] 절이 생겼습니다. 다만 지금은 2회차 11일·청산 2~3건이라 실제 돈으로 따라갈 표본이 아니라는 것도 같이 적었습니다.',
      '🔴 2026-09-14(월)부터 KRX 거래시간이 20시까지 연장됩니다(16~20시 애프터마켓 신설, 기존 시간외 단일가 폐지). 화면의 [장 마감] 판정이 정규장만 보고 있어서, 고치지 않으면 값이 움직이는 네 시간 내내 15:30 가격을 [종가]라고 표시할 참이었습니다. 시간외를 포함하도록 바꿨습니다. 나머지 대응(일봉이 애프터마켓을 포함하는지, 배치 시각표, 그날 바로 매수)은 월요일에 실제 데이터를 보고 정합니다.',
    ],
    qna: [
      {
        q: '추천이든 저점매수든 사는 시점에 환율·전쟁·금리 같은 대외 이슈가 터질 수 있는데, 그걸 감안 안 하고 계속 하락하는 종목을 그대로 다 사버리는 위험은 없나',
        a: '맞는 지적이었고, 방어선이 어디까지인지부터 확인했습니다. 지금은 종목 단위(추격매수 금지·손절·손절 후 28일 재매수 금지)와 업종 단위(최근 10일 성적 하위 3개 업종은 안 삼)까지이고 🔴 시장 전체를 보는 축은 없습니다. 코스피가 크게 빠져도 종목 조건만 맞으면 삽니다. 그래서 [시장이 최근 며칠 많이 빠졌으면 신규 매수를 쉰다]를 네 가지(1일 −2%, 5일 −3%, 5일 −5%, 없음)로 걸어 2년치를 앞뒤로 갈라 재봤습니다. 🔴 결과는 전부 기각입니다. 앞뒤 두 기간 모두 낙폭이 준 것은 하나뿐인데 그 개선이 0.05%p로 잡음 수준이고, 대가로 수익이 4분의 1씩 깎였습니다. 5일 가드는 저점매수(리스크)의 뒤 기간 낙폭을 −22.6%에서 −32.1%로 오히려 키웠습니다. 이유는 구조적입니다 — 저점매수는 하락에 사는 전략이라, 시장이 이미 빠진 뒤에 매수를 막는 것은 이 전략이 사야 할 바로 그 자리를 막는 것입니다. 다른 방향(아침에 갭으로 크게 빠져서 열린 날만 쉰다)은 아직 안 재봤습니다. 그리고 AI 추천 보고서에 환율·지정학을 넣어 점수에 반영하는 방법은 택하지 않았습니다 — 그 판단이 맞는지 확인할 방법이 없고, 그렇게 하면 다섯 계좌 실험이 [규칙]의 성적이 아니라 [그날 AI 판단]의 성적이 되기 때문입니다.',
      },
      {
        q: '저점매수 화면의 감시종목 50개는 무슨 의미인가 — 이 종목만 매수 대상인가',
        a: '맞습니다. 저점매수 두 계좌는 그 목록 안에서만 삽니다. 고점과 저점을 자주 오간 정도(왕복성)로 순위를 매겨 상위 N개를 뽑고, 하루 평균 300억 이상 거래되는 코스피 종목만 후보입니다. 30일마다 다시 뽑고, 매수 기준가는 매일 새 일봉으로 다시 계산합니다. 🔴 추천 3계좌는 이 목록과 무관합니다 — 그쪽은 코스피 전 종목(약 805개)을 훑습니다. 그래서 추천에는 뜨는데 감시 대상에는 없는 종목이 정상입니다. 그리고 이건 모의투자 계좌가 무엇을 살 수 있는지일 뿐이고, 직접 매매하실 종목을 제한하는 것이 아닙니다. 오늘 이 수를 50에서 100으로 올렸습니다.',
      },
      {
        q: '손익과 실현손익은 무엇이 다른가 — 중간에 싹 다 팔고 그만둔다면 어느 쪽을 기준으로 봐야 하나',
        a: '실현 손익은 이미 판 것의 확정된 손익이라 더 안 변하고, 평가 손익은 아직 안 판 것의 지금 손익이라 가격을 따라 움직입니다. 손익은 그 둘을 합친 것이고, 시드에 더하면 총자산입니다. 🔴 다 팔고 그만둘 거라면 볼 숫자는 실현 손익이 아니라 총자산 하나입니다. 파는 순간 평가 손익이 전부 실현 손익으로 바뀌어 합쳐지기 때문입니다. 실제 예로, 9월 11일 기준 저점매수(리스크) 계좌의 실현 손익은 +140만원인데 그때 전부 팔았다면 손에 남는 것은 +87만원이었습니다 — 차이 53만원이 아직 안 팔린 15종목에 들어 있었습니다. 실현 손익만 보면 그 53만원을 못 봅니다. 이건 고장이 아니라 규칙의 모양입니다. 저점매수는 +3~4% 오르면 바로 파니까 오른 종목은 실현 손익에 더해지고 안 오른 종목은 계좌에 남습니다. 그래서 승률이 82%(45건 중 37승)인데도 남은 종목은 마이너스일 수 있습니다. 그 둘을 따로 보면 오해하게 되어, 이번에 계좌 카드에 [승률 옆에 미실현]과 [지금 다 팔면 얼마]를 나란히 넣었습니다.',
      },
      {
        q: '2회차 누적수익률이 어느 순간부터 큰 차이가 안 나는데, 회차와 상관없이 그대로 두면 6개월 뒤에도 이 수익률이 유지될 수 있나',
        a: '유지되기 어렵습니다. 먼저 좋은 소식은, 같은 기간 시장(코스피 804종목 평균)이 +0.00%로 제자리인 동안 계좌들이 +1.2~5.8%를 냈다는 것입니다 — 장세가 아니라 실제 초과수익입니다. 다만 11일에 +5.8%를 그대로 이어가면 1년에 +250%인데, 2년치 검증에서 가장 좋았던 설정도 시장이 +47.8% 오르는 동안 +126%였습니다. 지금 속도는 그 두 배가 넘습니다. 짧은 구간의 좋은 숫자에는 우연이 섞여 있고, 실제로 앞 기간 1등이 뒤 기간 11등이 된 적이 있습니다. 그리고 추천 3계좌의 수익률이 붙어 있는 것은 고장이 아니라 설계입니다 — 세 계좌는 진입 기준이 완전히 같고 파는 방법만 다른데, 11일 동안 청산이 2~3건뿐이라 그 차이가 드러날 기회가 아직 없었습니다.',
      },
    ],
  },
  {
    version: '2026.09.11.1',
    date: '2026-09-11',
    changes: [
      '주도주에서 [거래대금] 열이 빠졌습니다. 전날 대비·등락률이 들어오면서 열이 다섯이 되어 폰에서 표가 가로로 밀렸습니다. 목록에 들어갈 50종목을 고르는 기준은 그대로 거래대금입니다 — 화면에 보이지 않을 뿐이고, 왜 그 기준인지는 제목 옆 물음표에 있습니다.',
    ],
  },
  {
    version: '2026.09.10.7',
    date: '2026-09-10',
    changes: [
      '주도주 정렬이 섹터 화면과 같아졌습니다. 카드 오른쪽 위의 [기본][현재가][거래대금] 버튼을 없애고, 열 제목을 누르면 그 열로 정렬되게 했습니다. 한 번 누르면 오름차순, 다시 누르면 내림차순, 세 번째에 원래 순서로 돌아옵니다. 같은 모양의 표를 화면마다 다른 방법으로 다루게 둘 이유가 없었습니다.',
      '두 화면 모두 [거래량]을 눌러도 정렬됩니다. 거래량은 현재가 아래 둘째 줄에 있어 제목이 [현재가 (종가) / 거래량] 한 덩어리였는데, 이제 두 이름이 각각 정렬 버튼입니다. 거래량을 열로 빼지 않은 것은 폰에서 칸이 하나 더 늘기 때문입니다.',
      '주도주에도 전일 대비와 등락률이 나옵니다. 섹터 화면과 같은 자리·같은 색(오르면 빨강, 내리면 파랑)입니다. 이 화면은 지금까지 마지막 종가만 보여줬는데, 이제 현재가도 30초마다 갱신됩니다 — 같은 종목이 화면마다 다른 값으로 보이면 어느 쪽을 믿어야 할지 알 수 없습니다. 거래량과 거래대금은 여전히 마지막 일봉의 값입니다.',
      '🔴 섹터 화면이 탭에 적힌 수만큼 전부 보여줍니다. 조선·방산·기계 탭에 46이라고 적혀 있는데 눌러 보면 30개만 나오고 있었습니다. 상한 자체를 없앴습니다 — 숫자를 다시 올려도 탭의 수와 목록 길이는 언제든 또 어긋나고, 어긋나는 순간 탭에 적힌 숫자가 거짓말이 됩니다. 가장 큰 업종이 74종목이라 부담도 없습니다.',
    ],
    qna: [
      {
        q: '섹터 탭에 46이라고 적혀 있는데 눌러 보면 30개만 나온다 — 한계가 있는 건가',
        a: '한계가 아니라 버그였습니다. 오늘 낮에 상한을 30에서 50으로 올렸는데 그것은 서버 쪽이고, 화면이 요청할 때 30이라는 숫자를 직접 적어 보내고 있어 서버가 열어둔 50이 쓰이지 못했습니다. 두 자리에 같은 숫자가 있으면 한쪽만 고쳤을 때 이렇게 됩니다. 이번에는 숫자를 올리는 대신 상한을 없앴습니다 — 탭에 적힌 수는 그 섹터의 종목 수라, 상한이 있는 한 둘은 언제든 어긋납니다.',
      },
    ],
  },
  {
    version: '2026.09.10.6',
    date: '2026-09-10',
    changes: [
      '🔴 [주도주] 탭이 생겼습니다. AI 모의투자와 섹터 사이입니다. 관리자가 고른 주도 섹터(지금은 반도체·AI와 전력기기·전선)의 거래대금 상위 50종목을 코스피 → 코스닥 순, 그 안에서 현재가가 높은 순으로 보여주고, 각 줄의 [담기]로 내 관심종목에 넣을 수 있습니다. 모든 계정이 같은 목록을 보며 개인 관심종목과는 별개라 50개 상한과 무관합니다. 종목을 저장해두지 않고 화면을 열 때마다 그 섹터의 현재 거래대금 상위를 뽑으므로 목록이 날마다 조금씩 바뀝니다. 왜 거래대금 순인지는 탭의 물음표에 적어뒀습니다.',
      '관심종목 카테고리 개수 제한(3개)이 없어졌습니다. 그 숫자를 정한 것이 화면이었는데(폰 375px에서 탭이 한 줄에 들어가는 상한), 탭을 옆으로 밀어 볼 수 있게 바꾸면서 근거가 사라졌습니다. [수정]과 [카테고리 관리] 버튼은 오른쪽에 고정이라 탭이 아무리 많아도 찾으러 스크롤할 일이 없습니다.',
      '섹터 화면이 한 업종에 50종목까지 보여줍니다(전에는 30). 코스닥이 들어오면서 업종당 종목이 늘어 반도체·AI 같은 큰 업종이 절반 넘게 잘리고 있었습니다. 업종마다 다른 수를 주지는 않았습니다 — 종목 수가 8~66개로 제각각이라 규칙을 만들면 오히려 "왜 이 업종만 적나"가 됩니다.',
      '섹터 화면에도 거래량이 나옵니다. 관심종목과 같은 자리(현재가 아래)이고, 같은 이유로 마지막 일봉의 값입니다.',
      '두 화면의 [현재가 (종가)] 제목이 [현재가 (종가) / 거래량]으로 바뀌었습니다. 값이 두 줄인데 제목이 하나라 아래 숫자가 거래량인지 거래대금인지 알 수 없었습니다.',
      '섹터 설명이 코스닥을 포함하도록 바뀌고, [자동 종목 선별(추천)은 코스피만 대상입니다]라는 한 줄이 붙었습니다. 섹터에 코스닥이 보인다고 추천까지 코스닥을 보는 것은 아닙니다.',
      'AI 모의투자 탭에 구분선이 생겼습니다. 추천 3계좌 · 저점매수 2계좌 · 기록(비교·회차 이력)은 하는 일이 달라 한 줄에 늘어서 있으면 구분이 안 됐습니다.',
      '비교 차트에서 저점매수와 저점매수(리스크)의 색이 갈립니다. 전에는 같은 보라에 실선/파선으로만 구분했는데, 곡선 다섯이 겹치는 구간에서 선 모양을 눈으로 좇기 어려웠습니다. 파선은 그대로 두고 색만 더했습니다 — 색각 이상에서도 갈리도록.',
      '매매 이력의 [일자]가 [일시]가 되어 분까지 나오고, 단가 아래에 수량을 곱한 금액이 붙습니다.',
      '저점매수 보유 종목에서 사다리가 2단 이상이면 체결 내역이 처음부터 펼쳐집니다. 1단은 접어 둡니다 — 보유의 85%가 1단이라 다 펼치면 목록만 길어지고, 펼쳐도 위에 적힌 평단과 같은 값이 한 줄 더 나올 뿐입니다.',
      '안전·분할익절·하이리스크 계좌 설명에 ATR이 무엇인지 예시와 함께 적었습니다. [+1.5 ATR 오르면 익절]만 있으면 ATR을 모르는 사람은 읽을 수 없었습니다.',
      '🔴 저점매수(리스크) 계좌의 익절폭이 +3%에서 +4%로 바뀌었습니다. 익절폭을 +2%부터 +12%까지 일곱 가지로 걸어 2년치를 앞뒤로 갈라 재봤더니 두 계좌의 답이 달랐습니다 — 저점매수는 +3%가 뒤 기간에서도 1위였는데, 리스크 계좌는 +3%가 앞 기간 1위이면서 뒤 기간 6위였습니다. +4%는 계좌가 출렁이는 폭이 −22.2%로 일곱 가지 중 가장 작으면서 수익도 더 높아(+99.1% vs +77.8%) 골랐습니다. 뒤 기간 1위인 +6%는 앞 기간 6위라 한쪽에만 좋아 잡지 않았습니다.',
      '⚠️ 그래서 두 저점매수 계좌의 차이가 둘이 됐습니다(매수 깊이 + 익절폭). 전에는 매수 깊이 하나만 달라 "무엇이 성적을 갈랐나"가 깨끗했는데, 이제 그 표만으로는 가릴 수 없습니다. 그리고 이 변경이 2회차(8/31~9/19) 도중에 적용돼 2회차 성적은 앞뒤가 섞입니다 — 회차끼리의 비교는 3회차부터 성립합니다.',
    ],
    qna: [
      {
        q: '안전 계좌 설명의 [+1.5 ATR 오르면 익절]은 금액이 아니라 거래량을 보고 판다는 뜻인가',
        a: '거래량이 아니라 가격 변동폭입니다. ATR은 그 종목이 하루에 보통 몇 원 움직이는지를 뜻하는 금액이라, 결국 금액으로 환산됩니다. ATR이 6,000원인 종목을 200,000원에 샀다면 209,000원(+4.5%)에 익절, 194,000원(−3.0%)에 손절입니다. %가 아니라 ATR로 긋는 이유는, 하루에 1% 움직이는 종목과 7% 움직이는 종목에 같은 +3%를 걸면 앞엣것은 사흘이 걸리고 뒤엣것은 반나절이면 닿기 때문입니다. 용어 설명 쪽이 정확했고 계좌 설명이 불친절했던 것이라, 예시를 넣어 고쳤습니다.',
      },
      {
        q: '관심종목에 보이는 거래량은 전날 것인가',
        a: '마지막 일봉의 값입니다. 장 마감 뒤에 보면 당일 것이고, 장중에 보면 전날 것입니다. 제목을 [현재가 (종가) / 거래량]으로 바꿔 그 사실이 보이게 했습니다.',
      },
      {
        q: '추천 3프로필에 회차 기간 제한이 없다면 언제 사고 언제 파나 — 나중에 이걸 따라 매매한다면 3주와 4주 중 어느 쪽이 나은가',
        a: '회차는 성적을 끊어 보는 단위일 뿐 매매 규칙이 아닙니다. 회차와 무관하게, 추천이 나온 다음 영업일 09:00부터 사고, 목표 도달·손절 도달·15영업일 경과 중 먼저 오는 것에 팝니다. 🔴 그래서 회차를 3주로 하면 안 됩니다 — 보유 한도가 15영업일(3주)이라 회차 막바지에 산 종목은 반드시 미청산으로 남아 그 회차 성적에 안 잡힙니다. 4주(20영업일)를 권합니다. 참고로 1회차는 7일밖에 안 됐고, 그래서 목표 도달이 0건이었습니다.',
      },
      {
        q: '저점매수의 빠른 익절이 좋긴 한데 더 오르는 경우도 있다 — 익절 기준을 바꿀 값어치가 있나',
        a: '이미 재놓은 표가 있었고, 결과가 계좌마다 갈립니다. 저점매수(리스크)는 현행 +3%가 학습 구간 1위지만 검증 구간에서는 6위입니다. 검증에서는 +4~6%가 모두 +3%보다 나았고, 특히 +4%는 낙폭이 −22.2%로 전체 최저이면서 수익도 99.1%(현행 77.8%)였습니다. 반면 저점매수(안전)는 +3%가 검증 1위입니다. 그래서 프로필을 새로 만들기보다 리스크 계좌만 +4%로 올리는 쪽을 권합니다 — 프로필을 늘리면 계좌당 표본이 더 쪼개지고, +4%는 "더 벌려다 더 잃는" 교환이 아니라 낙폭까지 함께 낮은 값이기 때문입니다. 적용은 9/19 회차가 끝난 뒤입니다. 회차 중간에 규칙을 바꾸면 그 회차 성적을 해석할 수 없게 됩니다.',
      },
    ],
  },
  {
    version: '2026.09.10.1',
    date: '2026-09-10',
    changes: [
      '관심종목 목록에 거래량이 나옵니다. 현재가 아래 작은 줄입니다. 🔴 실시간이 아니라 마지막 일봉의 값입니다 — 토스가 주는 현재가에는 거래량 항목이 아예 없어서, 등락률과 같은 방식으로 우리 일봉에서 가져옵니다. 좁은 칸에 23,314,005가 들어가지 않아 만·억에서 끊습니다(2,331만).',
      '종목 창과 관심종목에 업종이 표시됩니다. 종목 창은 이름 아래·가격 위, 목록은 종목코드 아래입니다. 🔴 이 자리는 원래 있었지만 늘 비어 있었습니다 — 종목 마스터의 업종 칸이 4,312종목 전부 비어 있었고, 우리가 직접 분류한 값은 다른 곳에 있었습니다. 그쪽을 보도록 고쳤습니다. 효성처럼 업종이 둘인 종목은 둘 다 나옵니다.',
      '섹터에 [로봇]과 [우주·위성]이 생기고 코스닥 종목이 들어왔습니다. 로봇 28종목(레인보우로보틱스·두산로보틱스 등), 우주·위성 8종목(쎄트렉아이·한국항공우주 등)입니다. 이름에 로보나 우주가 들어가도 사업이 다르면 넣지 않았습니다 — 우주일렉트로는 커넥터 회사이고 해성에어로보틱스는 항공 부품입니다.',
      'AI 모의투자에서 금액 합계를 봅니다. 매수가·현재가 아래에 수량을 곱한 금액이 붙고, 보유와 청산 목록 맨 아래에 합계 줄이 생깁니다. 현재가는 매수가보다 오르면 빨강, 내리면 파랑입니다. 다섯 계좌 모두에 적용됩니다.',
      '추천 3계좌(안전·분할익절·하이리스크)의 매수·매도 시각이 분까지 나옵니다. 저점매수 계좌는 원래 나오고 있었는데 이쪽은 날짜만 저장하고 있었습니다. 🔴 오늘 이전 거래는 계속 날짜만 보입니다 — 그때 시각을 남긴 적이 없어서, 09:00 같은 값으로 채우면 그건 기록이 아니라 추측이 됩니다.',
      '🔴 매일 조용히 실패하던 배치 두 개를 찾아 하나를 고쳤습니다. 시가총액 수집이 30일간 9번 죽고 있었는데, 저장 칸의 상한이 약 1,000조인데 삼성전자 시가총액이 약 1,612조라 들어가지 않았습니다. 그래서 시가총액이 한 번도 저장된 적이 없습니다(섹터 화면의 정렬에만 쓰여 매매에는 영향이 없었습니다). 칸을 넓혔습니다.',
      '🔴 코스닥 일봉을 매일 받습니다. 저녁 8시 30분에 따로 돕니다. 코스닥 데이터가 8월 13일에서 멈춰 있었는데 섹터에는 코스닥 종목이 들어가 있어, 한 달 전 가격이 섞일 참이었습니다. 낮 배치에 합치지 않은 이유는 그 배치 결과를 16:30 자동 종목 선별이 읽기 때문입니다 — 종목이 세 배가 되면 40분이 걸려 10분밖에 안 남습니다. 코스닥은 선별 대상이 아니라 급하지 않으므로 시간이 넉넉한 자리로 옮겼습니다.',
      '섹터 평균을 낼 때 오래된 값을 뺍니다. 마지막 가격이 일주일 넘게 갱신되지 않은 종목은 업종 평균에서 제외합니다. 거래정지나 상장폐지로 가격이 멈춘 종목이 그 업종을 대표하는 일을 막습니다.',
    ],
    qna: [
      {
        q: '실현손익과 손익(평가손익)은 무엇이 다른가',
        a: '실현손익은 판 것의 손익 합입니다. 이미 끝나서 더 안 변합니다. 그냥 손익(평가손익)은 아직 안 판 것의 지금 손익이라 가격이 움직이면 같이 움직입니다. 매매일지가 청산된 건과 보유 중인 건을 나눠 갖고 있고, 실현손익은 청산된 것만 더한 값입니다.',
      },
      {
        q: '추천이 장 마감 뒤에 나오는데 매수는 다음날 아침인가 — 그 사이 시간외 거래로 가격이 움직이는 것은 고려하고 있나',
        a: '🔴 맞습니다. 놓치고 있던 부분입니다. 추천은 16:30에 나오는데 모의투자 매수는 16시까지만 돌아서, 그날은 못 사고 다음날 09:00에 삽니다. 그 사이 시간외(16:30~20:00)와 장전(08:00~09:00)을 합쳐 최대 17시간이 비고, 그 시간에 실제로 거래가 됩니다 — 재보니 시간외 4만 봉 중 거래량이 0인 봉이 하나도 없었습니다. 다만 거래량이 정규장의 18% 수준이라 원하는 가격에 붙을지는 따로 재봐야 합니다. 열린 문제로 적어 뒀습니다.',
      },
      {
        q: '관심종목에 거래량도 함께 보여줄 수 있나 — 토스가 주지 않는 값인가',
        a: '짐작이 맞았습니다. 토스 현재가 API는 종목코드·현재가·통화·시각 네 가지만 주고 거래량 항목이 없습니다. 대신 우리가 매일 받는 일봉에 거래량이 있어서 그것을 씁니다. 등락률도 이미 같은 사정이라(토스가 전일 종가를 주지 않습니다) 같은 방식이고, 그래서 실시간이 아니라 마지막 일봉의 값입니다.',
      },
      {
        q: '알림에 가끔 뜨는 [배치 실패]는 무엇인가',
        a: '🔴 실제 버그 두 개였습니다. 하나는 시가총액 수집인데, 저장 칸이 약 1,000조까지만 담을 수 있어 삼성전자(약 1,612조)와 SK하이닉스(약 1,351조)가 들어가지 않아 30일간 9번 죽었습니다. 칸을 넓혀 고쳤습니다. 다른 하나는 일봉 수집인데 805종목 중 동양생명 한 종목이 8월 28일부터 계속 실패합니다. 상장폐지·거래정지·종목코드 변경 중 무엇인지 아직 확인하지 못했습니다.',
      },
      {
        q: '코스닥을 넣으면 데이터가 너무 많아지나 — 부하나 구조적 한계가 있나',
        a: '처음에 "이미 다 들어와 있고 매일 수집된다"고 말씀드렸는데 🔴 틀렸습니다. 종목 수만 세고 마지막 날짜를 안 봤습니다. 실제로는 8월 13일에서 멈춰 있었고 그 뒤로 한 번도 갱신되지 않았습니다. 부하는 낮 배치에 합치면 문제가 됩니다 — 종목이 805개에서 2,604개가 되면 40분이 걸리고, 그 결과를 16:30 선별이 읽어야 하는데 10분밖에 안 남습니다. 그래서 코스닥만 저녁 8시 30분에 따로 돌립니다. 선별 대상은 코스피 그대로입니다 — 코스닥을 선별에 넣으려면 점수 기준을 코스닥 분포로 다시 재야 합니다.',
      },
      {
        q: '저점매수(리스크)가 전력·전기·AI·반도체에서 주로 버는 것 같은데, 그 섹터에 특화된 프로필을 만들 값어치가 있나',
        a: '재봤더니 결론이 반대였습니다. 청산 목록만 보면 반드시 그렇게 보입니다 — 오른 건 팔리고 안 오른 건 남아 있기 때문입니다. 그래서 보유 중인 것까지 합쳐 같은 기간 시장 평균과 견줬더니, 전력기기·전선은 12개 업종 중 시장 1위(+4.63%)였고 우리 성적은 4위였습니다. 섹터가 좋았던 것이지 잘 고른 것이 아닙니다. 정작 잘한 곳은 시장이 밋밋했던(+0.64%) 조선·방산이었습니다. 그리고 우리가 사지 않은 섹터는 전부 마이너스였습니다(화장품 −11.17%) — 지금 필터가 이미 약한 업종을 피하고 있다는 뜻이고, 특화하면 그 섹터가 식었을 때 갈 곳이 없어집니다.',
      },
      {
        q: 'AI 프로필들이 남기는 회고에서 다음 회차에 쓸 만한 피드백을 얻을 수 있나',
        a: '사실 기록은 정확합니다. 다만 읽어 보니 네 건이 전부 같은 얘기를 하고 있었습니다 — "사다리가 1단에서 끝났다". 그것 하나는 진짜 발견이라 바로 재보는 계기가 됐지만, 그 이상은 나오기 어려워 보입니다. 자유롭게 쓴 글이라 "1단 비율 85%" 같은 것은 세어지지 않고, 그건 데이터베이스로 3초면 나옵니다. 회고를 살리려면 서술 대신 판정 항목(몇 단까지 갔나, 왜 안 갔나)을 붙이는 편이 낫겠습니다.',
      },
      {
        q: '사다리가 1단에서만 끝나는 것은 문제 아닌가 — 자본이 놀고 있는데',
        a: '🔴 제가 문제라고 했는데 재보니 아니었습니다. 2단 문턱을 1.0에서 0.7·0.5·0.3으로 낮춰 12가지를 재봤더니 낮출수록 낙폭이 단조롭게 커졌습니다(-7.3% → -11.0% → -12.6% → -16.0%). 자본을 다 쓰는 쪽(1단에 전액)은 낙폭이 1.2%p 줄지만 검증 구간 수익이 87.6%에서 45.0%로 반토막 났습니다. 노는 자본이 낭비가 아니라 방어였습니다. 목표(+3%)가 가까워 사다리가 안 열리는 것은 고장이 아니라 그 구조가 낙폭을 눌러주고 있던 것이라, 그대로 두기로 했습니다.',
      },
      {
        q: '추천할 때 대외 여건(유가·환율·지정학)도 보고 있나',
        a: '보고 있지 않습니다. AI에게 주는 것은 종목명·시장·최근 주가·지표와 그 종목의 뉴스·공시뿐입니다. 넣는 방법이 둘인데 성격이 다릅니다. 하나는 AI에게 "지금 환율 1,420원" 같은 배경을 주는 것인데, 그러면 글은 길어지지만 그 판단이 맞는지 우리가 확인할 방법이 없습니다. 다른 하나는 점수에 반영하는 것이고 이건 재볼 수 있습니다 — 예를 들어 "지수가 크게 빠진 날은 사지 않는다"는 지금 있는 진입 차단 장치에 축 하나를 더하는 정도라 백테스트로 검증됩니다. 유가·지정학은 데이터도 없고 과거 사례도 드물어 측정이 안 되므로 하지 않는 편이 맞습니다.',
      },
    ],
  },
  {
    version: '2026.09.03',
    date: '2026-09-03',
    changes: [
      '🔴 배포할 때마다 폰 알림이 꺼지던 것을 고쳤습니다. 앱을 갱신할 때 서비스 워커를 해제하는데 알림 구독이 거기에 붙어 있어 함께 사라졌고, 되살리는 장치가 일회용 표시 하나뿐이라 네 갈래로 끊어지고 있었습니다. 이제 [이 기기에서 받겠다]는 설정이 브라우저에 남아, 앱을 열 때마다 끊겼는지 확인하고 스스로 다시 연결합니다. 직접 끈 것은 되살아나지 않습니다. 이 설정은 계정마다 따로입니다 — 한 PC를 나눠 써도 A가 켜둔 것 때문에 B에게 구독이 옮겨가지 않습니다.',
      '주식 화면의 등락 색이 장 마감 뒤에도 유지됩니다. 어제까지는 마감이면 회색으로 죽였는데, 화면을 보는 시간의 대부분이 마감이라 목록이 통째로 회색이 되어 오른 종목과 내린 종목을 구분할 수 없었습니다. 색은 [지금 움직인다]가 아니라 [전일 대비 어느 쪽인가]를 말하는 것이라 유지하는 편이 맞습니다. 마감이라는 사실은 목록 머리와 [현재가 (종가)] 제목이 한 번만 알려줍니다.',
      '주식 화면의 가격에서 [원]을 뗐습니다. 한 열이 통째로 같은 통화라 줄마다 반복하면 자릿수를 읽는 데 방해가 되고, 좁은 폰 화면에서는 그 두 글자가 종목명을 밀어냈습니다. 가계부는 문장 안에 금액이 섞여 나오므로 그대로 [원]을 붙입니다.',
      '관심종목의 알림 표식이 종만 남았습니다. 어제 넣은 [🔔 220,000 +2]가 거래소 표시와 함께 종목명을 통째로 밀어내고 있었습니다. 이제 🔔 하나이고, 두 개 이상이면 [🔔 x3]처럼 개수만 붙습니다. 얼마에 걸었는지는 눌러서 봅니다.',
      '주식 표의 값이 제목과 같은 자리에 옵니다. 제목이 가운데인데 값만 오른쪽이라 어긋나 있던 것을 맞췄습니다. 종목명만 왼쪽입니다 — 이름은 길이가 제각각이라 가운데로 두면 들쭉날쭉합니다.',
      '표 제목이 다시 가운데 정렬입니다. 지난번에 값과 같은 방향(오른쪽)으로 맞췄는데, 실제 화면을 보니 가운데가 낫다는 판단이라 되돌렸습니다. 금액은 그대로 오른쪽 정렬이라 자릿수가 세로로 맞습니다.',
      '🔴 섹터 표에서 현재가가 한 줄에 두 번 나오던 것을 고쳤습니다. [현재가] 열과 [대비·등락률] 열이 같은 값을 각각 찍고 있었습니다.',
      '관심종목에 열 제목(종목 · 현재가 · 대비·등락률)이 생기고, 현재가와 대비가 제목에 맞춰 두 칸으로 나뉩니다.',
      '가격 알림 탭에서 울린 알림에 [다시 걸기]가 생겼습니다. 기준가는 그대로 두고 스위치만 올립니다 — 전에는 삭제뿐이라 같은 자리에 다시 걸려면 종목 창을 열어 숫자를 옮겨 적어야 했습니다.',
      '가격 알림 탭에서 종목 이름을 누르면 종목 창이 열립니다. 차트를 보고 그 자리에서 알림을 더 걸 수 있습니다.',
      '🔴 테스트 발송이 Slack으로도 갑니다. 실제 가격 알림은 폰과 Slack 둘 다로 나가는데 테스트는 폰에만 보내고 있어서, PC에서 눌러도 Slack이 조용한 것이 정상처럼 보였습니다. 결과 문구도 [켜둔 기기 3대 중 2대 도착 · Slack 발송함]처럼 바뀌어, 어느 기기에 안 왔는지 알 수 있습니다.',
      '관심종목 목록이 추천·섹터와 같은 모습이 됐습니다. 종목명 아래에 거래소와 종목코드, 오른쪽에 현재가 · 전일 대비 · 등락률이 함께 나옵니다. 메모를 적어둔 종목은 그 아래 한 줄이 더 붙습니다.',
      '관심종목의 알림 표식이 여러 개를 담습니다. 그전에는 한 종목에 5개를 걸어도 하나만 보였습니다. 이제 곧 울릴 것 하나를 값으로 보여주고 나머지는 [+2] 로 접습니다 — 예: 🔔 220,000 +2. 전부 울려 꺼졌으면 🔕 입니다.',
      'AI 모의투자에 [📚 회차 이력] 탭이 생겼습니다. 지난 회차를 눌러 펼치면 계좌별 실현 손익 · AI 종합 분석 · 그 기간에 쓰인 회고를 모아 봅니다. 기록은 원래 다 남아 있었습니다 — 초기화가 시드만 되돌리고 매매·회고는 건드리지 않았는데, 볼 화면이 없어서 1회차 것을 다시 읽을 수가 없었습니다.',
      '내 정보에 [알림 받는 곳]이 생겼습니다. 알림을 켠 기기가 목록으로 나오고 각각 끄고 켤 수 있으며, Slack도 따로 끕니다. 그전에는 폰·PC 구분 없이 모든 기기로 갔고, 끄려면 그 브라우저에 직접 가야 했습니다 — 회사 PC에 켜둔 알림을 집에서 끌 방법이 없었습니다. 기기 이름은 자동으로 붙고(예: 아이폰 (사파리)) 직접 고칠 수 있습니다.',
      '기기 [끄기]와 [지우기]가 다릅니다. 끄기는 연결을 남긴 채 알림만 건너뛰어 다시 켤 때 권한을 새로 묻지 않고, 지우기는 연결 자체를 끊습니다. 잃어버린 폰이나 반납한 PC를 정리할 때 지우기를 씁니다.',
    ],
  },
  {
    version: '2026.09.02',
    date: '2026-09-02',
    changes: [
      '추천과 섹터 화면의 종목이 증권사 앱처럼 보입니다. 종목명 아래에 거래소(KOSPI · 나스닥 등)와 종목코드가 함께 나오고, 오른쪽에 현재가 · 전일 대비 · 등락률이 한 덩어리로 붙습니다. 오르면 빨강, 내리면 파랑, 장이 닫혀 있으면 회색이고 [종가] 라고 적습니다. 봉(캔들)은 넣지 않았습니다 — 토스가 주는 현재가에는 그날의 시가 · 고가 · 저가가 없어서, 그리려면 어제 봉을 띄우거나 지어내야 합니다.',
      '가격 알림을 한 종목에 5개까지 걸 수 있습니다. 그전에는 저점 하나 · 목표 하나로 둘뿐이라, [220,000에 하나, 200,000에 하나] 같은 사다리를 만들 수 없었습니다. 위아래를 합쳐 5개입니다.',
      '종목 창을 열면 그 종목에 걸어둔 알림이 목록으로 보입니다. 안내 문구 자리에 기준가가 높은 순으로 쌓이고, 각 줄에 현재가 대비 몇 %인지와 삭제 버튼이 있습니다. 하나도 없으면 목록 대신 설명이 나옵니다.',
      '가격 알림 탭이 종목별로 묶입니다. 삼성전자 3개와 SK하이닉스 4개가 섞여 나오지 않고, 종목 카드 안에서 기준가가 높은 순입니다. 카드의 순서는 현재가가 기준가에 가장 가까운 종목 — 곧 울릴 것 같은 종목 — 이 위입니다.',
      '가격 알림을 정규장뿐 아니라 시간외에도 검사합니다. 장전 단일가와 장후 시간외에서도 가격은 움직이는데 그 시간에는 검사를 아예 안 하고 있었습니다. 장 시간은 토스가 주는 그날의 실제 시각을 따르므로 장이 순연되는 날에도 맞습니다. 다만 체결이 최근에 일어난 값일 때만 울립니다 — 마감가가 그대로 남아 있는 것을 [지금 도달했다] 로 잘못 알리면, 한 번 울린 알림은 꺼지므로 정작 다음 날 진짜 도달했을 때 안 울립니다.',
      '가계부 리포트의 금액에 자릿수 쉼표가 들어갑니다. [2237174원] 은 사람이 자릿수를 셀 수 없습니다. AI에게 쉼표 없이 쓰라고 지시하고 있던 것을 뒤집었고, 이미 저장된 옛 리포트도 화면에서 함께 고쳐 보여줍니다. 모의투자 회고와 종목 분석 등 AI가 쓴 글 전부에 적용됩니다.',
      '우측 하단 [지금 새로고침] 을 눌렀는데 아무 반응이 없던 것을 고쳤습니다. 새 버전 적용이 앱 내부에서 멈추면 [새로고침하는 중입니다…] 상태로 굳고 버튼까지 잠겨, 홈으로 가서 [최신으로 업데이트] 를 눌러야만 빠져나올 수 있었습니다. 이제 3.5초 안에 안 되면 스스로 강제 갱신으로 넘어가고, 그 사이 버튼을 다시 누르면 즉시 넘어갑니다.',
      '자동 종목 선별 결과의 [중간] 탭이 [분할익절] 로 바뀌었습니다. 계좌 이름은 지난번에 바꿨는데 이 탭만 옛 이름이 남아 있었습니다.',
      '관리자 → 모니터링의 [JVM 힙]이 실사용량을 보여줍니다. 그전 수치는 "지금 자리를 차지하고 있는 양"이라 버려졌지만 아직 청소되지 않은 것까지 포함돼 있었고, 그 값이 46%든 80%든 메모리가 정말 부족한 것인지 그냥 청소가 안 돈 것인지 가릴 수가 없었습니다. 이제 마지막 청소(GC) 직후에 실제로 남아 있던 양을 기준으로 막대를 그리고, 청소 전 값과 청소 횟수·누적 시간은 그 아래에 참고로 적습니다.',
    ],
  },
  {
    version: '2026.08.31',
    date: '2026-08-31',
    changes: [
      'AI 모의투자에 다섯 번째 계좌 [저점매수(리스크)]가 생겼습니다. 저점매수와 규칙이 완전히 같고 매수 기준가만 다릅니다 — 기본 저점매수는 최근 6개월 가격의 아래에서 20% 지점까지 내려와야 사는데, 이 계좌는 40% 지점에서 삽니다. 지금 설정으로는 감시 종목 50개 중 7개만 살 수 있는 자리에 있어 "웬만해선 사지 않는" 상태였고, 이 계좌는 18개까지 넓어집니다. 2년 검증에서 앞뒤 두 구간 모두 시장 수익률을 넘었습니다.',
      '비교 탭 곡선이 다섯 개가 됐습니다. [저점매수(리스크)]는 저점매수와 같은 보라색 점선입니다 — 색을 하나 더 늘리면 색맹인 분이 선을 구분할 수 없어, 형제 계좌는 색 대신 선 모양으로 나눴습니다.',
      '알림(종)을 눌렀을 때 창이 폰 화면 왼쪽으로 잘려 나가던 것을 고쳤습니다. 이제 폰에서는 화면 폭에 맞춰 펼쳐지고, PC에서는 종 아래에 그대로 붙습니다.',
      '업데이트 내역 창의 높이가 항상 같아졌습니다. 그전에는 그 페이지에 담긴 항목 수에 따라 창이 늘었다 줄었다 해서, 페이지를 넘길 때마다 버튼 위치가 움직였습니다.',
      '실험 회차가 끝나면 AI가 종합 분석을 씁니다. 다섯 계좌의 성적과 회고를 모아 [이번 회차 / 눈에 띈 것 / 재 볼 만한 것] 세 부분으로 정리해 비교 탭에 보여줍니다. 이 글은 매매 규칙을 바꾸지 않습니다 — AI가 무엇을 제안하든 실제로 반영하려면 과거 데이터로 먼저 검증해야 하고, 그 결과 지금 방식이 더 나은 경우가 실제로 있었습니다.',
      '[중간] 계좌 이름이 [분할익절]로 바뀌었습니다. 진입 조건이 세 계좌 모두 같아진 뒤로 이 계좌의 정체성은 "안전과 하이리스크의 사이"가 아니라 절반을 먼저 익절하고 나머지를 더 태우는 방식 하나인데, 이름이 그것을 가리고 있었습니다. 계좌와 기록은 그대로입니다.',
      '표의 숫자가 전 화면에서 자릿수가 맞게 정렬됩니다. 오른쪽 정렬 열은 숫자 열이라는 규칙을 표 컴포넌트가 직접 지키게 바꿨습니다 — 43곳이 개별로 빠뜨리고 있었습니다.',
      '가계부·관리자 화면의 표 제목이 값과 같은 방향으로 정렬됩니다. 제목은 가운데인데 값은 오른쪽이라 어느 제목이 어느 열인지 눈으로 따라가기 어려웠습니다(27곳).',
      '종목 상세의 [일자별 시세] 표가 좁은 화면에서 잘리지 않고 옆으로 넘겨집니다. [대비] 열의 "-1,200 (-2.35%)" 같은 값이 화면 밖으로 나가던 것입니다.',
      '계좌·보유 화면에서 종목명과 종목코드가 붙어 다닙니다. 좁은 화면에서 코드가 이름을 밀어 두 줄로 접히던 것을 고쳤습니다.',
      '추천 3계좌(안전·분할익절·하이리스크)도 꺼지는 업종을 건너뜁니다. 저점매수가 쓰던 장치를 같은 값(최근 10거래일 성적 하위 3개 업종)으로 켰습니다. 추천은 그대로 나오고 사지만 않습니다. 2년 검증에서 세 계좌 모두 계좌가 출렁이는 폭이 줄었고, 안전 계좌는 뒤 기간 수익이 마이너스에서 플러스로 바뀌며 시장 평균을 넘었습니다.',
      '섹터 분류가 넓어졌습니다. 거래대금 상위 종목 40개가 업종을 갖게 됐고(LG전자 포함), 덮는 비율이 25%에서 30%로 올랐습니다. 업종을 모르는 종목은 위 필터가 그냥 지나치기 때문에, 이 확장이 필터가 실제로 듣게 만드는 전제였습니다.',
      '추천 기준을 완화하는 것이 나은지 재봤고, 지금 기준을 유지하기로 했습니다. 최근 변동성이 큰 종목들이 걸러지는 것은 사실이지만(조건을 넓히면 추천이 533건에서 최대 1,935건으로 늘어납니다), 늘어난 추천의 질이 나빠 2년 검증에서 수익이 무너지고 계좌가 출렁이는 폭도 깊어졌습니다. 오르는 종목을 사서 밀릴 때 파는 방식(추세형)도 함께 재봤지만 앞뒤 두 기간 모두 손실이었습니다.',
      '보유 종목이 최근에 산 것부터 표시됩니다. 다섯 계좌 모두 적용되며, 추천 3계좌는 그전까지 오래된 순이라 새로 산 종목이 목록 맨 아래에 묻혀 있었습니다.',
      '저점매수의 재매수 금지(28일)가 회차 시작 이후만 셉니다. 회차가 3주인데 금지 기간이 28일이라, 그대로 두면 이전 회차에서 손절한 종목이 새 회차로 최대 28일까지 이월됐습니다. 시드와 보유를 전부 초기화했는데 매매 제약만 살아남아, 감시 대상 중 몇 개가 이유 없이 빠진 것처럼 보이는 상태였습니다. 28일이라는 값 자체는 그대로입니다.',
      '도움말 [용어]에 <손익 · 실현 손익>과 <반등 익절 · 구조 이탈>이 생겼습니다. 손익은 판 것과 아직 들고 있는 것을 합친 값이고 실현 손익은 팔아서 확정된 것만이라, 그 차이가 아직 안 판 종목의 평가입니다. 구조 이탈은 "이 종목은 일정 범위를 오간다"는 전제가 깨졌다는 뜻으로, 매수 기준가보다 2 ATR 아래로 뚫고 내려간 경우입니다.',
      '저점매수 두 계좌에 매수 시각이 표시됩니다. 청산 이력에 [매수 → 매도] 시각이 분 단위로 나오고, 보유 중인 종목도 첫 매수 시각이 목표·손절과 같은 줄에 보입니다. 그전에는 매도 시각만 있고 매수는 [사다리 보기]를 눌러야 알 수 있었습니다. 이 계좌는 평균 이틀 만에 회전하고 같은 날 사고파는 일도 있어 날짜만으로는 부족합니다.',
      '모의투자 보유·청산과 섹터 표에서도 표식([조건 이탈 매수], [선별 대상])이 종목명 아래 줄로 내려갔습니다. 감시 대상 표와 같은 방식으로 맞췄습니다.',
      '🔴 저점매수 감시 대상이 100종목으로 두 배가 되어 같은 종목이 두 번씩 나오던 것을 고쳤습니다. 새로 생긴 [저점매수(리스크)] 계좌의 목록이 기존 저점매수 목록과 한데 섞여 보인 것으로, 실제 매매는 각자의 50종목으로 정상 진행됐고 화면 조회만 잘못돼 있었습니다.',
      '표에서 "13,610원"이 "13,610"과 "원"으로 갈라지던 것을 고쳤습니다. 지난 업데이트에서 한글이 단어 중간에 끊기지 않게 하면서 함께 걸어둔 규칙이 숫자에까지 적용된 탓입니다. 금액·수익률은 이제 단위까지 한 덩어리로 붙어 있고, 표 제목도 두 줄로 갈라지지 않습니다.',
      '감시 대상 표에서 [매수 구간] 표식이 종목명 아래 줄로 내려갔습니다. 같은 줄에 있으면 좁은 화면에서 종목명이 밀려 접혔습니다.',
      '[자동 종목 선별 결과]에 저점매수 계열이 왜 없는지 적었습니다. 그쪽은 매일 하는 자동 선별을 쓰지 않고, 왕복이 잦은 50종목을 한 달간 고정해 두고 그 안에서만 사고팔기 때문입니다.',
      '비교 탭에 [시장] 기준선이 생겼습니다. 같은 기간에 코스피 종목을 똑같은 금액씩 나눠 사서 그냥 들고 있었으면 얼마였는지를 회색 점선으로 함께 그립니다. 이 선보다 아래에 있는 계좌는, 아무것도 고르지 않고 그냥 다 샀을 때보다 못한 것입니다. 이 선이 없으면 "1등 +20%"를 보고 좋아할 수 있는데 그 기간 시장이 +50%였다면 다섯 다 진 것이라, 순위만으로는 알 수 없었습니다. 코스피 지수와는 다릅니다 — 지수는 큰 회사가 더 크게 반영되지만 우리 계좌들은 종목마다 비슷한 금액을 넣기 때문에 기준선도 그렇게 맞췄습니다. 2026-08-31부터 쌓입니다.',
      '비교 탭 도움말에 [이 비교로 알 수 있는 것과 없는 것]이 생겼습니다. 순위를 보여주는 화면이라 "1등이 정답"으로 읽히기 쉬운데, 실제로 과거 검증에서 앞 1년의 1등이 뒤 1년에는 손실로 뒤집힌 적이 있습니다. 수익률보다 최대 낙폭을 먼저 보라는 것과, 회차 기간은 3~4주가 적당한 이유(단기매매라 몇 달을 묶으면 국면 하나만 보게 됩니다)도 함께 적었습니다.',
      '다섯 번째 계좌 이름이 [저점매수 공격]에서 [저점매수(리스크)]로 바뀌었습니다. 앞의 세 계좌가 안전 · 중간 · 하이리스크라 "리스크"가 이미 쓰이는 말인데, "공격"은 여기서만 쓰는 새 말이라 어느 쪽이 더 위험한지 견주기 어려웠습니다. 계좌와 기록은 그대로입니다.',
      '저점매수 도움말에 "왜 +3%에 다 파나"가 생겼습니다. 절반만 팔고 나머지를 더 태우는 방식 9가지와, 익절 기준을 +2%부터 +12%까지 7가지로 걸어 2년치를 앞뒤로 갈라 검증했습니다. 지금 방식(+3% 전량)이 전부 이겼고, 수익뿐 아니라 계좌가 출렁이는 폭까지 나빠졌습니다. 절반을 남겨두면 그 돈이 묶여 다음에 다른 종목이 싸졌을 때 사지 못하는 것이 이유입니다. 매매 규칙은 바뀌지 않았습니다.',
    ],
  },
  {
    version: '2026.08.30',
    date: '2026-08-30',
    changes: [
      '저점매수가 꺼지는 업종을 건너뜁니다. 같은 업종 종목들의 최근 10거래일 성적을 평균 내, 가장 밀리는 3개 업종은 아무리 싸 보여도 사지 않습니다. 시스템은 뉴스를 읽지 못해 "값이 왜 떨어졌는지"를 모르는데, 업종이 통째로 밀리는 것은 볼 수 있어서 그것을 대신 봅니다. 2년 검증에서 계좌가 출렁이는 폭이 30~40% 줄었습니다.',
      '섹터에 [화장품·뷰티]와 [전력기기·전선]이 생겼습니다. 화장품은 소비재와 인터넷·게임에 흩어져 있었고, 에이피알·달바글로벌은 잘못 분류돼 있었습니다. 전력기기·전선은 세 곳에 갈려 있던 것을 모았습니다.',
      'AI 모의투자 도움말에 [지금 설정]이 생겼습니다. 각 계좌가 지금 어떤 값으로 돌아가는지 표로 먼저 보여주고, 왕복성·분위·ATR 같은 용어는 아래 [용어]에서 풀어 설명합니다.',
      '앱이 새 버전을 스스로 찾아 갱신합니다. 그전에는 홈 화면에 추가한 앱이 며칠씩 옛 화면을 보여줘, 앱을 지우고 사이트 데이터를 지운 뒤 다시 추가해야 했습니다. 이제 앱을 다시 열 때마다 확인하고, 새 버전이 있으면 알린 뒤 새로고침합니다. 로그인은 유지됩니다.',
      '홈 화면 버전 아래 [최신으로 업데이트] 버튼이 생겼습니다. 자동 갱신이 안 될 때 캐시를 비우고 최신 상태로 되돌립니다.',
      'AI 모의투자에 [회차] 개념이 생겼습니다. 초기화할 때 시드와 함께 기간을 정하고, 기간이 끝나면 보유를 모두 정리해 성적을 확정한 뒤 계좌별 회고를 만듭니다.',
      '비교 탭의 [월 목표 대비] 카드가 [성적 순위]로 바뀌었습니다. 정해둔 목표에 견주는 대신 계좌끼리 수익률로 줄 세우고, 매매가 몇 건뿐일 때는 순위가 흔들릴 수 있다고 함께 적습니다.',
      '한글이 단어 가운데서 끊기지 않습니다. 좁은 화면에서 "실현 손 / 익을"처럼 글자 한가운데가 잘리던 것이 "실현 / 손익을"로 어절 단위로 접힙니다.',
      '업데이트 내역이 목록과 상세로 나뉘었습니다. 버전을 5개씩 넘겨 보고 누르면 그 날짜의 변경 내용이 열립니다. 닫기 버튼이 항상 보이는 자리에 있습니다 — 그전에는 폰에서 내역이 화면을 꽉 채워 닫기 어려웠습니다.',
      '[최신으로 업데이트]가 실제로 새 버전이 있을 때만 뜹니다. 최신인데도 늘 떠 있어 혼란스럽던 것을 고쳤습니다. 자동 갱신이 안 될 때를 위한 수동 버튼은 업데이트 내역 안으로 옮겼습니다.',
      '모의투자 청산 목록이 폰에서 읽기 편해졌습니다. 보유일을 날짜 줄에 합쳐 칸을 넓혔습니다.',
      '매매일지의 AI 지시문이 본문 글꼴로 바뀌고 줄 간격이 넓어졌습니다.',
      '새 버전이 나오면 알림(종)에 뜹니다. 눌러서 업데이트 내역을 보면 사라지고, 읽음은 계정마다 따로 기록됩니다.',
      '저점매수 감시 대상이 하루 빨리 갱신됩니다. 그전에는 장 마감 후 들어온 새 일봉을 다음 날 장이 열려야 반영해, 하루를 늦게 썼습니다. 이제 장 마감 후(16:05)에 바로 반영하고 장 시작 한 시간 전(08:00)에 한 번 더 확인합니다. 장이 열리자마자 매매에 들어갈 수 있습니다.',
    ],
  },
  {
    version: '2026.08.28',
    date: '2026-08-28',
    changes: [
      '저점매수가 더 과감해졌습니다. 감시 대상이 30종목에서 50종목으로 늘고, 거래대금 기준이 하루 1,000억에서 300억으로 낮아져 중형주도 들어옵니다. 종목당 쓰는 돈도 계좌의 15%에서 20%로 올렸습니다. 더 자주 사고 더 벌지만 계좌가 출렁이는 폭도 함께 커지는 맞바꿈입니다.',
      '저점매수에 회고가 생겼습니다. 사다리 한 묶음이 끝날 때마다 무엇을 왜 사고팔았는지 정리합니다. 분할매수는 이 전략의 규칙이므로 실수로 적지 않습니다.',
      '비교 탭에 저점매수 곡선이 함께 그려집니다. 네 계좌를 한 화면에서 견줍니다.',
      '감시대상·섹터 탭에서 가격 관련 열을 눌러 정렬할 수 있습니다.',
      '도움말 물음표가 눈에 띄게 바뀌었습니다. 배경색이 생겨 본문에 묻히지 않습니다.',
    ],
  },
  {
    version: '2026.08.21',
    date: '2026-08-21',
    changes: [
      '혜택 공간이 열렸습니다. 쓰고 있는 카드·통신사·멤버십을 등록해 두면, 앞으로 여기에 걸리는 혜택만 골라 알려드립니다. 카드번호는 저장하지 않습니다.',
      '혜택 대시보드에 다가오는 혜택이 뜹니다. T day처럼 반복되는 혜택을 관리자가 규칙으로 등록하면, 내 수단에 걸리는 것만 다음 날짜와 함께 보입니다.',
      '폰 알림이 생겼습니다. 내 정보에서 알림을 켜면 이 기기로 알림이 옵니다. 아이폰은 홈 화면에 추가한 앱에서 열어야 켤 수 있습니다.',
      '혜택 알림이 옵니다. 등록된 혜택의 며칠 전 알림일 아침에, 그 수단을 쓰는 사람의 폰과 Slack 으로 알려드립니다.',
      'AI 모의투자에 매매 이력이 생겼습니다. 매수와 매도를 시간순으로 한 줄기로 봅니다. 실현손익이 예수금에 합산되어 재투자된다는 것도 계좌 카드에 적었습니다.',
      '모의투자 회고가 장중 매도 건에서 멈추던 문제와, 계좌 초기화 확인 문구가 맞게 입력해도 거절되던 문제를 고쳤습니다.',
      'AI 모의투자가 실시간으로 삽니다. 하루 세 번 정해진 시각에 사던 것이 장중 매분 감시로 바뀌어, 눌린 자리에 들어온 순간 삽니다. 판 뒤에 다시 눌리면 그날 안에 한 번 더 살 수 있습니다.',
      '시세가 30초마다 갱신됩니다. 서버 수집 주기가 1분에서 30초로 짧아졌습니다.',
      '가격 알림에 목표가가 생겼습니다. 이제 저점(↓)과 목표(↑) 둘 다 걸 수 있고, 울리면 폰 알림과 Slack 으로 함께 옵니다.',
      '가격 알림 탭이 생겼습니다. 걸어둔 알림의 기준가·현재가·남은 거리를 한 화면에서 봅니다.',
      '매매일지가 생겼습니다. 토스 계좌의 실제 체결을 먼저 산 것부터 짝지어 왕복 거래로 묶고, 수수료·세금을 뺀 순손익을 보여줍니다. 추천과 이어진 매매와 스스로 산 매매의 성적을 갈라 봅니다.',
      '섹터 탭이 생겼습니다. 반도체·AI, 2차전지, 바이오 등 10개 테마로 코스피 종목을 묶어 시가총액 순으로 봅니다.',
      'AI 모의투자에 네 번째 계좌 [저점매수]가 생겼습니다. 오르내림이 잦은 대형주 50개를 정해두고, 각자의 매수 기준가(최근 6개월 가격의 하위 20% 지점) 아래에 오면 나눠 사고 +3% 반등하면 파는 것을 반복합니다. 실제 매매 방식을 그대로 옮긴 계좌이며, 2.1년 과거 검증에서 +57.8%(최대 하락 -9.4%)였습니다.',
      '저점매수 감시 대상 표가 좋아졌습니다. 현재가·매수 기준가·기준가까지 열을 눌러 정렬할 수 있고, ? 를 누르면 각 열의 뜻을 설명합니다. 현재가는 30초마다 갱신되며 갱신 시각이 함께 표시됩니다.',
      'AI 모의투자 탭에 ? 도움말이 생겼습니다. 각 계좌가 어떻게 종목을 고르고 언제 사고파는지, 입문자 눈높이로 설명합니다.',
      '섹터 화면의 종목을 누르면 차트·관심종목 담기가 되는 상세창이 열립니다.',
      '종목 상세창에 [일자별 시세]가 생겼습니다. 날짜별 종가·전일 대비·거래량을 스크롤로 이어 볼 수 있고, 최대 1년까지 제공합니다.',
      '추천 카드에 현재가와 등락률이 표시됩니다. 추천 시점의 진입가와 지금 가격을 카드에서 바로 비교할 수 있습니다.',
      '매매일지가 [계좌·보유·체결] 안으로 옮겨졌습니다. 실제 체결·평단·손익은 본인만 봐야 하는 정보인데, 그전에는 추천 화면과 같은 권한으로 열려 있어 계정이 있는 가족 모두에게 보였습니다.',
      '바코드 보관함이 생겼습니다. 통신사 할인 바코드처럼 매장에서 내미는 이미지를 혜택 탭에 모아 두고, 눌러서 전체화면으로 바로 띄웁니다. 자주 쓴 것이 위로 올라오고, 혜택 대시보드에서도 바로 꺼낼 수 있습니다. 화면이 꺼지지 않게 잡아두지만 밝기는 폰에서 올려야 합니다.',
    ],
  },
  {
    version: '2026.08.20',
    date: '2026-08-20',
    changes: [
      '미국주식이 들어왔습니다. 단기매매 화면이 [국내주식|해외주식] 탭으로 갈리고, NYSE·NASDAQ·AMEX 종목을 검색해 관심종목에 담고 차트를 볼 수 있습니다. 가격은 달러로 표시됩니다.',
      '미국 주식 가격에 원화가 함께 표시됩니다. 매시 정각에 저장하는 참고 환율로 환산한 어림값이며, 기준 시각이 함께 보입니다.',
      '차트의 좌우 빈 공간이 사라지고, 가격 눈금과 마우스오버 가격에 콤마가 붙습니다.',
      '길게 쌓이는 목록이 나뉘어 보입니다. 모의투자 청산·회고는 페이지로 넘기고, 추천 목록은 10건씩 더 보기로 펼칩니다.',
      '모의투자 초기화의 시드 금액 입력에도 콤마가 자동으로 붙습니다.',
      'AI 모의투자가 추격매수를 하지 않습니다. 추천 기준가보다 0.5 ATR 이상 오른 가격에서는 사지 않고, 같은 날 다시 눌리면 그때 삽니다.',
      '저점매수 알림을 미국 종목에도 걸 수 있습니다. 미국 정규장(한국시간 밤 10시 반~새벽 5시 무렵)에 기준단가 도달을 검사해 Slack 으로 알립니다.',
      '추천 프로필이 3종이 됩니다. 안전 · 중간(신설) · 하이리스크가 같은 종목을 고르되 목표·손절만 다릅니다. 2.1년 백테스트에서 하이리스크 전용 종목군이 계속 지는 것으로 나와 폐기했습니다.',
      'AI 모의투자가 장중에 목표·손절을 잡습니다. 하루 3번만 보던 것이 매분 감시로 바뀌어, 선에 닿는 순간 팝니다. 매수는 여전히 하루 3번입니다.',
      '중간 프로필이 분할매도를 합니다. 절반을 +1.5 ATR 에서 익절하고 손절선을 본전으로 올린 뒤, 나머지가 +3.0 ATR 을 노립니다.',
      'AI 모의투자가 교체를 합니다. 자본이 묶여 새 추천을 못 살 상황이면 가장 오래된 보유를 팔아 자리를 만듭니다. 2.1년 백테스트에서 수익과 낙폭이 모두 개선됐습니다.',
      '저점매수 알림이 생겼습니다. 관심종목에 기준단가를 걸어두면 장중에 그 가격 이하로 내려오는 순간 Slack 으로 알립니다.',
      '추천 점수가 제대로 매겨집니다. 왕복성과 거래량 항목의 기준선이 잘못 잡혀 있어 대부분의 종목이 이 두 항목에서 0점을 받고 있었습니다.',
      '종목 배경 요약이 실제로 쌓이기 시작합니다. 같은 종목이 다시 추천되면 이미 읽은 기사를 다시 읽지 않습니다.',
    ],
  },
  {
    version: '2026.08.19',
    date: '2026-08-19',
    changes: [
      '알림이 생겼습니다. 헤더의 종을 누르면 새 추천 · 모의투자 매매 · (관리자) 배치 실패를 모아 봅니다.',
      'AI 모의투자가 실제로 굴러가기 시작했습니다. 두 추천 기준이 각각 2,000만원으로 매매하고, 비교 탭에서 나란히 볼 수 있습니다.',
      '단기매매 화면 위에 [추천] [AI 모의투자] [지난 추천] 탭이 생겼습니다.',
      '관심종목 시세에 기준 시각이 함께 뜹니다. 화면 숫자는 보통 30~60초 전 값입니다.',
      '추천이 종목의 왕복 성격과 거래량 급증을 함께 봅니다. 여러 번 오르내린 종목을 더 높게 칩니다.',
      '(관리자) 오류 탭이 생겼습니다. 서버 오류를 화면에서 바로 봅니다.',
      'Slack 알림이 단타와 자산관리 채널로 갈립니다. 섞여 오던 것이 각자 채널로 갑니다.',
    ],
  },
  {
    version: '2026.08.17',
    date: '2026-08-17',
    changes: [
      '추천이 두 기준으로 갈렸습니다. 🛡️ 안전은 덜 먹더라도 자주, ⚡ 하이리스크는 크게 노립니다.',
      '관심종목에 카테고리가 생겼습니다. 한 종목을 여러 카테고리에 담을 수 있고 순서도 바꿀 수 있습니다.',
      '자동 로그인 여부가 로그인 화면에 표시됩니다.',
      '금액 입력칸에 콤마가 자동으로 붙습니다.',
      '화면 하단에 버전이 표시됩니다.',
    ],
  },
  {
    version: '2026.08.16',
    date: '2026-08-16',
    changes: [
      '지난 추천 화면이 생겼습니다. 추천이 실제로 맞았는지 누적 성적을 봅니다.',
      '종목 검색과 관심종목, 차트가 생겼습니다.',
      '계좌·보유·체결 화면이 생겼습니다.',
      '지표에 물음표를 누르면 설명이 뜹니다.',
    ],
  },
];

/** 화면 하단 버전(2026.08.19.5)에서 내역을 찾을 때 쓸 앞부분. */
export function changelogVersionOf(appVersion: string | null | undefined): string | null {
  if (!appVersion) {
    return null;
  }
  // 2026.08.19.5 → 2026.08.19
  const parts = appVersion.split('.');
  return parts.length >= 3 ? parts.slice(0, 3).join('.') : appVersion;
}

/**
 * 이 내역 항목이 지금 쓰는 버전인가 — "지금 쓰는 버전" 배지. (2026-10-19 병합 전 화면 점검)
 *
 * 🔴 9/10 뒤 항목은 버전이 네 칸(2026.10.18.89 — 하루 여러 번 배포)이라 앞 세 칸(changelogVersionOf)만 견주면 한 번도 안 맞았다.
 *   네 칸 그대로 같으면 맞다. 옛 항목(세 칸 · 2026.08.16 — 하루 한 항목이던 때)은 그날 어느 빌드든 맞다.
 */
export function isCurrentChangelog(entryVersion: string, appVersion: string | null | undefined): boolean {
  if (!appVersion) {
    return false;
  }
  return entryVersion === appVersion || entryVersion === changelogVersionOf(appVersion);
}
